import React from "react";
import Modal from "@cloudscape-design/components/modal";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import Container from "@cloudscape-design/components/container";
import Alert from "@cloudscape-design/components/alert";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import Textarea from "@cloudscape-design/components/textarea";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { achievementEditModalStore } from "../stores/achievementEditModal";
import Header from "@cloudscape-design/components/header";
import Grid from "@cloudscape-design/components/grid";
import DatePicker from "@cloudscape-design/components/date-picker";
import { gameStore } from "../stores/games";
import { configStore } from "../stores/config";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import { RISK_MODEL_OPTIONS, getRiskModelSelectedOptions } from "../helpers/riskModelOptions";
import { PARTNER_PAYMENT_TYPE_OPTIONS } from "../helpers/cohortConditions";

import DailyProgressiveTab, {
	INITIAL_DAILY_PROGRESSIVE_CONFIG,
	INITIAL_NEW_MILESTONE,
	formatGamesForSelect,
	getGamesByProvider,
} from "./AchievementDailyProgressiveTab";
import {
	buildAchievementTransferData,
	buildEditAchievementStateFromTransfer,
	copyTextToClipboard,
	findChangedLockedAchievementFields,
	getAchievementTransferJson,
	parseAchievementTransferEnvelope,
	stringifyAchievementTransfer,
} from "../helpers/achievementTransferJson";
import { formatChestPrizeType } from "../helpers/chestPrizeTypes";

const parseChestRewardConditions = (value, fallback = {}) => {
	if (Array.isArray(value)) {
		return value;
	}

	if (value && typeof value === "object") {
		return value;
	}

	if (typeof value === "string" && value.trim().length) {
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	}

	return fallback;
};

const getChestRewardsPayload = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions, []);

	if (Array.isArray(parsed)) {
		return parsed;
	}

	if (Array.isArray(parsed?.prizes)) {
		return parsed.prizes;
	}

	return [];
};

const getChestPrizeIdFromRewardConditions = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions);

	if (Array.isArray(parsed)) {
		return null;
	}

	return parsed?.chestPrizeId || null;
};

const getChestTypeFromRewardConditions = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions);

	if (Array.isArray(parsed)) {
		return "";
	}

	return parsed?.type || "";
};

const normalizeChestPrizeValue = (value) => {
	if (Array.isArray(value)) {
		return value.map((entry) => normalizeChestPrizeValue(entry));
	}

	if (value && typeof value === "object") {
		return Object.keys(value)
			.sort()
			.reduce((accumulator, key) => {
				accumulator[key] = normalizeChestPrizeValue(value[key]);
				return accumulator;
			}, {});
	}

	return value;
};

const getChestPrizeSignature = ({ type, rewardConditions }) =>
	JSON.stringify({
		type: type || "",
		rewardConditions: normalizeChestPrizeValue(getChestRewardsPayload(rewardConditions)),
	});

const findMatchingChestPrize = (items = [], { type, rewardConditions }) => {
	const chestPrizeId = getChestPrizeIdFromRewardConditions(rewardConditions);
	if (chestPrizeId) {
		return (items || []).find((item) => Number(item.id) === Number(chestPrizeId)) || null;
	}

	const targetSignature = getChestPrizeSignature({ type, rewardConditions });

	return (
		(items || []).find(
			(item) =>
				getChestPrizeSignature({
					type: item.type,
					rewardConditions: item.rewardConditions,
				}) === targetSignature
		) || null
	);
};

const formatChestRewardsSummary = (rewardConditions, fallback = "Chest config") => {
	const rewards = getChestRewardsPayload(rewardConditions);
	if (!rewards.length) {
		return fallback;
	}

	const totalProbability = rewards.reduce(
		(sum, reward) => sum + (Number(reward.probability) || 0),
		0
	);

	return `Chest • ${rewards.length} rewards • ${totalProbability}%`;
};

const getChestPrizeDisplayLabel = (items = [], { type, rewardConditions, fallback = "Chest config" }) => {
	const match = findMatchingChestPrize(items, { type, rewardConditions });

	return match ? `${match.name} (${match.id})` : formatChestRewardsSummary(rewardConditions, fallback);
};

const buildChestPrizeOption = (item) => ({
	value: String(item.id),
	label: `${item.name} (${item.id})`,
	description: formatChestPrizeType(item.type),
	chestPrize: item,
});

const depositType = [
	{ value: "crypto", label: "Crypto" },
	{ value: "fiat", label: "Fiat" },
	{ value: "both", label: "Both" },
];

const FREESPINS_PROVIDER_OPTIONS = [{ label: "Endorphina", value: "endorphina", type: 10 }];
const CHEST_REWARD_OPTIONS = [
	{ value: "money", label: "Money" },
	{ value: "tickets", label: "Tickets" },
	{ value: "freespins", label: "Freespins" },
];

const createChestRewardDraft = () => ({
	id: crypto.randomUUID(),
	rewardType: null,
	rewardValue: "",
	probability: "",
	rewardConditions: {},
	providerSelected: null,
	gameSelected: null,
	betAmount: "",
	expiresAt: "",
});

class AchievementEditModal extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			imageError: "",
			editingRow: null,
			editingValue: "",
			levelError: "",
			fsEditingRow: null,
			fsEditing: null,
			chestEditingRow: null,
			chestEditingRewards: [],
			chestEditingPrizeId: null,
			chestEditingPrizeSelected: null,
			chestEditingType: "",
			chestEditingMatchMissing: false,
			rewardTypeEdit: null,
			rewardTypeValue: "",
			cohortGroups: [],
			newConditionByGroupId: {},
			newChestRewards: [],
			newChestType: "",
			newChestPrizeId: null,
			newChestPrizeSelected: null,
			availableLevelSpinsGames: [],
			newSpinsProvider: null,
			newSpinsGame: null,
			newSpinsAmount: null,
			spinsEditingRow: null,
			spinsEditing: null,
			dailyProgressiveConfig: { ...INITIAL_DAILY_PROGRESSIVE_CONFIG },
			newMilestone: { ...INITIAL_NEW_MILESTONE },
			availableMilestoneGames: [],
			dailyConditionsSpinsGames: [],
			jsonText: "",
			jsonError: "",
			jsonDirty: false,
			jsonImagePreview: "",
		};
	}

	getGroupKeyByModule = (module) => {
		if (module === "deposit") return "depositFilters";
		if (module === "withdrawal") return "withdrawalFilters";
		if (module === "registration") return "registrationFilters";
		return "profileFilters";
	};

	getCohortModuleOptions = () => [
		{ value: "deposit", label: "Deposit" },
		{ value: "withdrawal", label: "Withdrawal" },
		{ value: "registration", label: "Registration" },
		{ value: "profile", label: "Profile" },
	];

	getLogicalOperatorOptions = () => [
		{ value: "AND", label: "AND" },
		{ value: "OR", label: "OR" },
	];

	getCohortFieldOptions = () => [
		{ value: "id", label: "Id", type: "string" },
		{ value: "risk_model", label: "Risk model", type: "multiselect", specialType: "riskModel" },
		{ value: "verify", label: "Verify", type: "select", specialType: "boolean" },
		{ value: "sweepstakes_count", label: "Sweepstakes_count", type: "count" },
		{ value: "tasks_count", label: "Tasks count", type: "count" },
		{ value: "email", label: "Email", type: "select", specialType: "boolean" },
		{ value: "phone", label: "Phone", type: "select", specialType: "boolean" },
		{ value: "tg", label: "Telegram", type: "select", specialType: "boolean" },
		{ value: "partnerName", label: "Partner Name", type: "string" },
		{ value: "paymentType", label: "Payment Type", type: "select", specialType: "partnerPaymentType" },
		{ value: "trafficType", label: "Traffic Type", type: "string" },

		{ value: "deposit_date", label: "Deposit date", type: "count" },
		{ value: "deposit_date_crypto", label: "Deposit crypto date", type: "count" },
		{ value: "deposit_date_fiat", label: "Deposit fiat date", type: "count" },
		{ value: "deposit_count", label: "Deposit count", type: "count" },
		{ value: "deposit_count_fiat", label: "Deposit fiat count", type: "count" },
		{ value: "deposit_count_crypto", label: "Deposit crypto count", type: "count" },
		{ value: "deposit_from", label: "Deposit from", type: "date" },
		{ value: "deposit_to", label: "Deposit to", type: "date" },
		{ value: "deposit_crypto_from", label: "Deposit crypto from", type: "date" },
		{ value: "deposit_crypto_to", label: "Deposit crypto to", type: "date" },
		{ value: "deposit_fiat_from", label: "Deposit fiat from", type: "date" },
		{ value: "deposit_fiat_to", label: "Deposit fiat to", type: "date" },
		{ value: "deposit_amount_to", label: "Deposit amount to", type: "count" },
		{ value: "deposit_amount_from", label: "Deposit amount from", type: "count" },
		{ value: "deposit_amount_crypto_to", label: "Deposit amount crypto to", type: "count" },
		{ value: "deposit_amount_crypto_from", label: "Deposit amount crypto from", type: "count" },
		{ value: "deposit_amount_fiat_to", label: "Deposit amount fiat to", type: "count" },
		{ value: "deposit_amount_fiat_from", label: "Deposit amount fiat from", type: "count" },
		{ value: "deposit_type", label: "Deposit type", type: "select", specialType: "cryptoOrFiat" },

		{ value: "withdrawal_date", label: "Withdrawal date", type: "count" },
		{ value: "withdrawal_date_crypto", label: "Withdrawal crypto date", type: "count" },
		{ value: "withdrawal_date_fiat", label: "Withdrawal fiat date", type: "count" },
		{ value: "withdrawal_count", label: "Withdrawal count", type: "count" },
		{ value: "withdrawal_count_fiat", label: "Withdrawal fiat count", type: "count" },
		{ value: "withdrawal_count_crypto", label: "Withdrawal crypto count", type: "count" },
		{ value: "withdrawal_from", label: "Withdrawal from", type: "date" },
		{ value: "withdrawal_to", label: "Withdrawal to", type: "date" },
		{ value: "withdrawal_crypto_from", label: "Withdrawal crypto from", type: "date" },
		{ value: "withdrawal_crypto_to", label: "Withdrawal crypto to", type: "date" },
		{ value: "withdrawal_fiat_from", label: "Withdrawal fiat from", type: "date" },
		{ value: "withdrawal_fiat_to", label: "Withdrawal fiat to", type: "date" },
		{ value: "withdrawal_amount_to", label: "Withdrawal amount to", type: "count" },
		{ value: "withdrawal_amount_from", label: "Withdrawal amount from", type: "count" },
		{ value: "withdrawal_amount_crypto_to", label: "Withdrawal amount crypto to", type: "count" },
		{ value: "withdrawal_amount_crypto_from", label: "Withdrawal amount crypto from", type: "count" },
		{ value: "withdrawal_amount_fiat_to", label: "Withdrawal amount fiat to", type: "count" },
		{ value: "withdrawal_amount_fiat_from", label: "Withdrawal amount fiat from", type: "count" },
		{ value: "withdrawal_type", label: "Withdrawal type", type: "select", specialType: "cryptoOrFiat" },

		{ value: "spins", label: "Spins", type: "count" },
		{ value: "lvl_club", label: "Club", type: "count" },
		{ value: "referer", label: "Referer", type: "count" },
		{ value: "balance", label: "Balance", type: "count" },
		{ value: "registration_from", label: "Registration from", type: "date" },
		{ value: "registration_to", label: "Registration to", type: "date" },
		{ value: "registration_date", label: "Registration user days", type: "count" },
	];

	getCohortFieldOptionsByModule = (module) => {
		const fields = this.getCohortFieldOptions();

		if (module === "deposit") return fields.filter((f) => f.value.startsWith("deposit_"));
		if (module === "withdrawal") return fields.filter((f) => f.value.startsWith("withdrawal_"));
		if (module === "registration") return fields.filter((f) => f.value.startsWith("registration_"));

		return fields.filter((f) => !f.value.startsWith("deposit_") && !f.value.startsWith("withdrawal_") && !f.value.startsWith("registration_"));
	};

	updateNewCondition = (groupId, patch) => {
		this.setState((prev) => ({
			newConditionByGroupId: {
				...(prev.newConditionByGroupId || {}),
				[groupId]: {
					...(prev.newConditionByGroupId?.[groupId] || {}),
					...patch,
				},
			},
		}));
	};

	resetNewCondition = (groupId) => {
		this.setState((prev) => ({
			newConditionByGroupId: {
				...(prev.newConditionByGroupId || {}),
				[groupId]: {},
			},
		}));
	};

	isConditionValueEmpty = (value) => value === null || value === "" || (Array.isArray(value) && value.length === 0);

	normalizeGroupConditions = (conditions, groupId) => {
		const groupConditions = (conditions || []).filter((c) => c.groupId === groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

		if (!groupConditions.length) return conditions;

		const firstId = groupConditions[0].id;
		return (conditions || []).map((c) => {
			if (c.groupId !== groupId) return c;
			if (c.id === firstId) return { ...c, logicalOperator: null };
			if (!c.logicalOperator) return { ...c, logicalOperator: "AND" };
			return c;
		});
	};

	normalizeCohortConditions = (rawConditions) => {
		const fieldMetaByValue = new Map(this.getCohortFieldOptions().map((f) => [f.value, f]));

		const deriveModule = (field) => {
			if (!field) return "profile";
			if (field.startsWith("deposit_")) return "deposit";
			if (field.startsWith("withdrawal_")) return "withdrawal";
			if (field.startsWith("registration_")) return "registration";
			return "profile";
		};

		const conditions = Array.isArray(rawConditions) ? rawConditions.filter(Boolean) : [];
		if (!conditions.length) {
			const groupId = crypto.randomUUID();
			return {
				groups: [
					{
						groupId,
						module: "deposit",
						groupKey: "depositFilters",
						groupLogicalOperator: null,
					},
				],
				conditions: [],
			};
		}

		const hasAllGroupIds = conditions.every((c) => c && typeof c === "object" && c.groupId);

		if (!hasAllGroupIds) {
			const hasAnyGroupKey = conditions.some((c) => c && typeof c === "object" && c.groupKey);

			if (hasAnyGroupKey) {
				const groupsByKey = new Map();
				const groupKeysOrder = [];

				conditions.forEach((c) => {
					const field = c.field || c.fieldLabel || "";
					const module = deriveModule(field);
					const groupKey = c.groupKey || this.getGroupKeyByModule(module);
					if (groupsByKey.has(groupKey)) return;

					const groupId = crypto.randomUUID();
					const groupLogicalOperator = groupKeysOrder.length === 0 ? null : "AND";

					groupsByKey.set(groupKey, { groupId, module, groupKey, groupLogicalOperator });
					groupKeysOrder.push(groupKey);
				});

				const groups = groupKeysOrder.map((groupKey, idx) => {
					const g = groupsByKey.get(groupKey);
					return {
						groupId: g.groupId,
						module: g.module || "deposit",
						groupKey: g.groupKey,
						groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
					};
				});

				const groupMetaByKey = new Map(groups.map((g, idx) => [g.groupKey, { ...g, groupOrder: idx }]));
				const conditionOrderByGroupId = new Map();

				let normalizedConditions = conditions
					.map((c) => {
						const field = c.field || c.fieldLabel || "";
						const module = deriveModule(field);
						const groupKey = c.groupKey || this.getGroupKeyByModule(module);
						const meta = groupMetaByKey.get(groupKey);
						if (!meta) return null;

						const prevOrder = conditionOrderByGroupId.get(meta.groupId) ?? -1;
						const conditionOrder = prevOrder + 1;
						conditionOrderByGroupId.set(meta.groupId, conditionOrder);

						const fm = fieldMetaByValue.get(field);

						return {
							...c,
							id: c.id || crypto.randomUUID(),
							field,
							fieldLabel: c.fieldLabel || fm?.label || c.field || field || "",
							conditionalType: c.conditionalType || fm?.type,
							specialType: typeof c.specialType !== "undefined" ? c.specialType : fm?.specialType,
							groupId: meta.groupId,
							module: meta.module,
							groupKey: meta.groupKey,
							groupOrder: meta.groupOrder,
							groupLogicalOperator: meta.groupOrder === 0 ? null : meta.groupLogicalOperator || "AND",
							conditionOrder: typeof c.conditionOrder === "number" ? c.conditionOrder : conditionOrder,
							logicalOperator: conditionOrder === 0 ? null : c.logicalOperator || "AND",
						};
					})
					.filter(Boolean);

				groups.forEach((g) => {
					normalizedConditions = this.normalizeGroupConditions(normalizedConditions, g.groupId);
				});

				return { groups, conditions: normalizedConditions };
			}

			const groupsByModule = new Map();
			const modulesOrder = [];

			conditions.forEach((c) => {
				const module = deriveModule(c.field || c.fieldLabel || "");
				if (groupsByModule.has(module)) return;

				const groupId = crypto.randomUUID();
				const groupLogicalOperator = modulesOrder.length === 0 ? null : "AND";

				groupsByModule.set(module, {
					groupId,
					module,
					groupKey: this.getGroupKeyByModule(module),
					groupLogicalOperator,
				});
				modulesOrder.push(module);
			});

			const groups = modulesOrder.map((module, idx) => {
				const g = groupsByModule.get(module);
				return {
					...g,
					groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
				};
			});

			const groupMetaByModule = new Map(groups.map((g, idx) => [g.module, { ...g, groupOrder: idx }]));
			const conditionOrderByGroupId = new Map();

			let normalizedConditions = conditions.map((c) => {
				const module = deriveModule(c.field || c.fieldLabel || "");
				const meta = groupMetaByModule.get(module);

				const prevOrder = conditionOrderByGroupId.get(meta.groupId) ?? -1;
				const conditionOrder = prevOrder + 1;
				conditionOrderByGroupId.set(meta.groupId, conditionOrder);

				const field = c.field || c.fieldLabel || "";
				const fm = fieldMetaByValue.get(field);

				return {
					...c,
					id: c.id || crypto.randomUUID(),
					field,
					fieldLabel: c.fieldLabel || fm?.label || c.field || field || "",
					conditionalType: c.conditionalType || fm?.type,
					specialType: typeof c.specialType !== "undefined" ? c.specialType : fm?.specialType,
					groupId: meta.groupId,
					module: meta.module,
					groupKey: c.groupKey || meta.groupKey,
					groupOrder: meta.groupOrder,
					groupLogicalOperator: meta.groupOrder === 0 ? null : meta.groupLogicalOperator || "AND",
					conditionOrder: typeof c.conditionOrder === "number" ? c.conditionOrder : conditionOrder,
					logicalOperator: conditionOrder === 0 ? null : c.logicalOperator || "AND",
				};
			});

			groups.forEach((g) => {
				normalizedConditions = this.normalizeGroupConditions(normalizedConditions, g.groupId);
			});

			return { groups, conditions: normalizedConditions };
		}

		const groupMeta = new Map();
		const firstSeenOrder = new Map();

		conditions.forEach((c, idx) => {
			if (!c || typeof c !== "object") return;
			if (!firstSeenOrder.has(c.groupId)) firstSeenOrder.set(c.groupId, idx);

			const field = c.field || c.fieldLabel || "";
			const module = c.module || deriveModule(field);

			if (!groupMeta.has(c.groupId)) {
				groupMeta.set(c.groupId, {
					groupId: c.groupId,
					module,
					groupKey: c.groupKey || this.getGroupKeyByModule(module),
					groupLogicalOperator: c.groupLogicalOperator || null,
					groupOrder: typeof c.groupOrder === "number" ? c.groupOrder : firstSeenOrder.get(c.groupId),
				});
			}

			const meta = groupMeta.get(c.groupId);
			if (!meta.module) meta.module = module;
			if (typeof c.groupOrder === "number") meta.groupOrder = c.groupOrder;
			if (c.groupKey) meta.groupKey = c.groupKey;
			if (c.groupLogicalOperator) meta.groupLogicalOperator = c.groupLogicalOperator;
		});

		const groups = [...groupMeta.values()]
			.sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0))
			.map((g, idx) => ({
				groupId: g.groupId,
				module: g.module || "deposit",
				groupKey: g.groupKey || this.getGroupKeyByModule(g.module || "deposit"),
				groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
			}));

		const indexByGroupId = new Map(groups.map((g, idx) => [g.groupId, idx]));
		const nextOrderByGroupId = new Map();

		let normalizedConditions = conditions
			.filter((c) => c && typeof c === "object" && indexByGroupId.has(c.groupId))
			.map((c) => {
				const groupIndex = indexByGroupId.get(c.groupId);
				const group = groups[groupIndex];

				const prevOrder = nextOrderByGroupId.get(c.groupId) ?? -1;
				const conditionOrder = typeof c.conditionOrder === "number" ? c.conditionOrder : prevOrder + 1;
				nextOrderByGroupId.set(c.groupId, Math.max(prevOrder, conditionOrder));

				const field = c.field || c.fieldLabel || "";
				const fm = fieldMetaByValue.get(field);

				return {
					...c,
					id: c.id || crypto.randomUUID(),
					field,
					fieldLabel: c.fieldLabel || fm?.label || c.field || field || "",
					conditionalType: c.conditionalType || fm?.type,
					specialType: typeof c.specialType !== "undefined" ? c.specialType : fm?.specialType,
					groupId: group.groupId,
					module: group.module,
					groupKey: c.groupKey || group.groupKey || this.getGroupKeyByModule(group.module),
					groupOrder: groupIndex,
					groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
					conditionOrder,
					logicalOperator: conditionOrder === 0 ? null : c.logicalOperator || "AND",
				};
			});

		groups.forEach((g) => {
			normalizedConditions = this.normalizeGroupConditions(normalizedConditions, g.groupId);
		});

		return { groups, conditions: normalizedConditions };
	};

	addCohortGroup = () => {
		const groupId = crypto.randomUUID();

		this.setState((prev) => {
			const groups = [...(prev.cohortGroups || [])];
			const groupLogicalOperator = groups.length === 0 ? null : "AND";

			return {
				cohortGroups: [
					...groups,
					{
						groupId,
						module: "deposit",
						groupKey: "depositFilters",
						groupLogicalOperator,
					},
				],
			};
		});
	};

	updateCohortGroup = (groupId, patch) => {
		const nextGroups = (this.state.cohortGroups || []).map((g) => (g.groupId === groupId ? { ...g, ...patch } : g));

		this.setState({ cohortGroups: nextGroups });

		const A = this.props.achievementEditModal;
		const conditions = A.conditions || [];
		const updatedConditions = conditions.map((c) =>
			c.groupId === groupId
				? {
						...c,
						...(typeof patch.module !== "undefined" ? { module: patch.module } : {}),
						...(typeof patch.module !== "undefined" ? { groupKey: this.getGroupKeyByModule(patch.module) } : {}),
						...(typeof patch.groupLogicalOperator !== "undefined" ? { groupLogicalOperator: patch.groupLogicalOperator } : {}),
				  }
				: c,
		);

		this.props.setAchievementEditModal({
			...A,
			conditions: updatedConditions,
		});
	};

	changeCohortGroupModule = (groupId, module) => {
		const nextGroups = (this.state.cohortGroups || []).map((g) => (g.groupId === groupId ? { ...g, module, groupKey: this.getGroupKeyByModule(module) } : g));

		this.setState({ cohortGroups: nextGroups });

		const A = this.props.achievementEditModal;
		const nextConditions = (A.conditions || []).filter((c) => c.groupId !== groupId);

		this.props.setAchievementEditModal({
			...A,
			conditions: nextConditions,
		});

		this.resetNewCondition(groupId);
	};

	removeCohortGroup = (groupId) => {
		const nextGroups = (this.state.cohortGroups || [])
			.filter((g) => g.groupId !== groupId)
			.map((g, idx) => ({
				...g,
				groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
			}));

		const groupIndexById = new Map(nextGroups.map((g, idx) => [g.groupId, idx]));

		const A = this.props.achievementEditModal;
		const nextConditions = (A.conditions || [])
			.filter((c) => c.groupId !== groupId)
			.filter((c) => groupIndexById.has(c.groupId))
			.map((c) => {
				const groupOrder = groupIndexById.get(c.groupId);
				const group = nextGroups[groupOrder];

				return {
					...c,
					module: group.module,
					groupKey: group.groupKey || this.getGroupKeyByModule(group.module),
					groupOrder,
					groupLogicalOperator: groupOrder === 0 ? null : group.groupLogicalOperator || "AND",
				};
			});

		this.setState((prev) => {
			const nextNewConditionByGroupId = { ...(prev.newConditionByGroupId || {}) };
			delete nextNewConditionByGroupId[groupId];

			return {
				cohortGroups: nextGroups,
				newConditionByGroupId: nextNewConditionByGroupId,
			};
		});

		this.props.setAchievementEditModal({
			...A,
			conditions: nextConditions,
		});
	};

	addCohortCondition = (group, groupIndex) => {
		const A = this.props.achievementEditModal;
		const conditions = A.conditions || [];
		const groupConditions = conditions.filter((c) => c.groupId === group.groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

		const newCondition = this.state.newConditionByGroupId?.[group.groupId] || {};
		if (!newCondition.field || this.isConditionValueEmpty(newCondition.value)) return;

		let value = newCondition.value;
		if (newCondition.conditionalType === "select") {
			value = newCondition.value.value || newCondition.value;
		} else if (newCondition.conditionalType === "boolean") {
			value = newCondition.value ? 1 : 0;
		}

		const nextOrder = groupConditions.length ? Math.max(...groupConditions.map((c) => c.conditionOrder ?? 0)) + 1 : 0;

		const logicalOperator = groupConditions.length ? newCondition.logicalOperator || "AND" : null;

		const item = {
			...newCondition,
			id: crypto.randomUUID(),
			value,
			logicalOperator,
			conditionOrder: nextOrder,
			groupId: group.groupId,
			module: group.module,
			groupKey: group.groupKey || this.getGroupKeyByModule(group.module),
			groupOrder: groupIndex,
			groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
			deleteId: crypto.randomUUID(),
		};

		const nextConditions = this.normalizeGroupConditions([...conditions, item], group.groupId);

		this.props.setAchievementEditModal({
			...A,
			conditions: nextConditions,
		});
		this.resetNewCondition(group.groupId);
	};
	async componentDidMount() {
		this.initModal();
	}

	componentDidUpdate(prevProps) {
		const prevRewardType = prevProps.achievementEditModal.rewardType?.value;
		const currentRewardType = this.props.achievementEditModal.rewardType?.value;

		if (
			prevRewardType !== "chest" &&
			currentRewardType === "chest" &&
			!this.props.achievementEditModal.chestPrizeOptions.length
		) {
			this.loadChestPrizeOptions({
				matchCurrent: true,
				currentType: this.props.achievementEditModal.chestType,
				currentRewardConditions: this.props.achievementEditModal.chestRewards || [],
			});
		}
	}

	componentWillUnmount() {
		if (this.props.achievementEditModal.image) {
			URL.revokeObjectURL(this.props.achievementEditModal.image);
		}
	}

	safeJsonParse(object, defaultValue = []) {
		try {
			return JSON.parse(object);
		} catch {
			return defaultValue;
		}
	}

	initModal = () => {
		try {
			this.props.setAchievementEditModal((prev) => ({ ...prev, loaded: false }));

			const levels = this.fetchProgress(this.safeJsonParse(this.props.achievementEditModal.levelsConditions));
			const cohortRaw = this.safeJsonParse(this.props.achievementEditModal.cohortConditions);
			const normalizedCohort = this.normalizeCohortConditions(cohortRaw);

			let providerSelected = null;
			let gameSelected = null;
			let availableGames = [];
			let betAmount = "";
			let expiresAt = "";
			let chestRewards = [];
			let chestPrizeId = null;
			let providerSpinsSelected = null;
			let gameSpinsAmount = null;
			let gameSpinsSelected = null;
			let availableSpinsGames = [];
			let amount = null;
			let triggerType = "";

			const A = this.props.achievementEditModal;

			if (A.rewardType?.value === "freespins") {
				const rewardConditions = JSON.parse(A.rewardConditions || "{}");

				providerSelected = { label: "Endorphina", value: "endorphina" };

				if (providerSelected.value === "endorphina") {
					const gameState = this.props.game;
					if (!gameState?.loaded || !gameState?.data) {
						this.props.Interface.addNoty("Error", "Please reload page game state not loading");
					} else {
						const endorphinaGames = gameState.data.filter((g) => g.providerID === 10 && g.active === 1);
						availableGames = endorphinaGames.map((g) => ({
							id: g.gameID,
							description: g.name,
							name: g.name,
							gameID: g.gameID,
							providerID: g.providerID,
						}));
					}
				}

				if (rewardConditions?.gameId && availableGames.length > 0) {
					const foundGame = availableGames.find((game) => game.id === Number(rewardConditions.gameId));
					if (foundGame) {
						gameSelected = {
							label: foundGame.description,
							value: foundGame.description,
							gameId: foundGame.id,
							game: foundGame,
						};
					}
				}

				betAmount = Number(rewardConditions.betAmount) || "";
				expiresAt = rewardConditions.expiresAt || "";
			} else if (A.rewardType?.value === "chest") {
				chestRewards = getChestRewardsPayload(A.rewardConditions);
				chestPrizeId = getChestPrizeIdFromRewardConditions(A.rewardConditions);
			}

			if (A.sourceType?.value === "spins") {
				const spinsConditionsStr = A.triggerConditions || "{}";
				let spinsConditions = typeof spinsConditionsStr === "string" ? JSON.parse(spinsConditionsStr) : spinsConditionsStr;

				if (Object.keys(spinsConditions).length > 0) {
					spinsConditions = spinsConditions["spins"];
					gameSpinsAmount = Number(spinsConditions.amount);
					if (spinsConditions.providerId && spinsConditions.gameId) {
						const providers = this.props.config.game.options.provider || [];
						providerSpinsSelected = providers.find((p) => p.type === spinsConditions.providerId);
						availableSpinsGames = [];
						gameSpinsSelected = null;
						if (providerSpinsSelected && this.props.game?.loaded && this.props.game?.data) {
							const filteredGames = this.props.game.data.filter((game) => game.providerID === providerSpinsSelected.type && game.active === 1);
							availableSpinsGames = filteredGames.map((game) => ({
								id: game.gameID,
								description: game.name,
								name: game.name,
								gameID: game.gameID,
								providerID: game.providerID,
							}));
							if (spinsConditions.gameId) {
								const foundGame = availableSpinsGames.find((game) => game.id === Number(spinsConditions.gameId));
								if (foundGame) {
									gameSpinsSelected = {
										label: foundGame.description,
										value: foundGame.description,
										gameId: foundGame.id,
										game: foundGame,
									};
								}
							}
						}
					}
				}
			}

			if (A.triggerConditions) {
				const triggerConditionsParsed = this.safeJsonParse(A.triggerConditions, {});
				if (triggerConditionsParsed.deposit || triggerConditionsParsed.withdrawal) {
					const tcKey = triggerConditionsParsed.deposit ? "deposit" : "withdrawal";
					const tc = triggerConditionsParsed[tcKey];
					amount = tc.amount / 100;
					triggerType = depositType.find((t) => t.value === tc.type) || "";
				}
			}

		let dailyProgressiveConfigState = {
			breakOnMissedDay: false,
			resetType: "full",
			penaltyDays: 0,
			dailyConditions: {
				type: "deposit",
				deposit: { amount: 0, paymentType: "both" },
				spins: { count: 0, minBet: 0, gameId: null, providerId: null },
			},
			milestones: [],
		};

		if (A.type?.value === "daily_progressive" && A.dailyProgressiveConfigRaw) {
			try {
				const parsedConfig = typeof A.dailyProgressiveConfigRaw === "string"
					? JSON.parse(A.dailyProgressiveConfigRaw)
					: A.dailyProgressiveConfigRaw;

				dailyProgressiveConfigState = {
					breakOnMissedDay: parsedConfig.breakOnMissedDay ?? false,
					resetType: parsedConfig.resetType || "full",
					penaltyDays: parsedConfig.penaltyDays || 0,
					dailyConditions: {
						type: parsedConfig.dailyConditions?.type || "deposit",
						deposit: {
							amount: parsedConfig.dailyConditions?.deposit?.amount / 100 || 0,
							paymentType: parsedConfig.dailyConditions?.deposit?.paymentType || "both",
						},
						spins: {
							count: parsedConfig.dailyConditions?.spins?.count || 0,
							minBet: parsedConfig.dailyConditions?.spins?.minBet || 0,
							gameId: parsedConfig.dailyConditions?.spins?.gameId || null,
							providerId: parsedConfig.dailyConditions?.spins?.providerId || null,
						},
					},
					milestones: (parsedConfig.milestones || []).map((m) => ({
						id: crypto.randomUUID(),
						day: m.day,
						rewardType: m.rewardType,
						rewardValue: m.rewardType === "money" ? Number.parseInt(m.rewardValue, 10) / 100 :  m.rewardValue,
						rewardConditions: m.rewardConditions || {},
					})),
				};
			} catch (e) {
				console.error("Failed to parse dailyProgressiveConfig", e);
			}
		}

		let dailyConditionsSpinsGames = [];
		const dailySpinsProviderId = dailyProgressiveConfigState.dailyConditions?.spins?.providerId;
		if (dailySpinsProviderId) {
			const gameState = this.props.game;
			if (gameState?.loaded && gameState?.data) {
				dailyConditionsSpinsGames = getGamesByProvider(gameState.data, dailySpinsProviderId);
			} else {
				this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			}
		}

		const topLevelChestType =
			A.rewardType?.value === "chest" ? getChestTypeFromRewardConditions(A.rewardConditions) : "";

		this.setState({
			cohortGroups: normalizedCohort.groups,
			newConditionByGroupId: {},
			dailyProgressiveConfig: dailyProgressiveConfigState,
			newMilestone: { ...INITIAL_NEW_MILESTONE },
			availableMilestoneGames: [],
			dailyConditionsSpinsGames,
			newChestRewards: [],
			newChestType: "",
			newChestPrizeSelected: null,
		});

		this.props.setAchievementEditModal((prev) => ({
			...prev,
			levels,
			conditions: normalizedCohort.conditions,
			availableGames,
			providerSelected,
			gameSelected,
			betAmount,
			expiresAt,
			chestRewards,
			chestType: topLevelChestType,
			chestPrizeId,
			chestPrizeSelected: null,
			chestPrizeMatchMissing: false,
			gameSpinsAmount,
			providerSpinsSelected,
			gameSpinsSelected,
			availableSpinsGames,
			loaded: true,
			amount,
			triggerType,
		}));

		if (A.rewardType?.value === "chest") {
			this.loadChestPrizeOptions({
				matchCurrent: true,
				currentType: topLevelChestType,
				currentRewardConditions: A.rewardConditions,
			});
		} else if (
			(levels || []).some((level) => level.rewardType === "chest") ||
			(dailyProgressiveConfigState.milestones || []).some((milestone) => milestone.rewardType === "chest")
		) {
			this.loadChestPrizeOptions();
		}
	} catch (e) {
		console.error("Init failed", e);
		this.props.setAchievementEditModal((prev) => ({ ...prev, loaded: true }));
	}
};

	fetchCohort = (data) => {
		return data.map((cohort) => ({ ...cohort, fieldLabel: cohort.field }));
	};

	fetchProgress = (data) => {
		const sourceType = this.props.achievementEditModal.sourceType?.value;

		return data.map((element) => {
			let trigger_type;
			let amount;
			let providerId;
			let gameId;
			let spinsAmount;
			let freespins;

			if (sourceType === "deposit" || sourceType === "withdrawal") {
				const raw = typeof element.triggerConditions === "string" ? JSON.parse(element.triggerConditions || "{}") : element.triggerConditions || {};

				const tc = raw[sourceType] || {};
				trigger_type = tc.type;
				if (tc.amount != null) amount = tc.amount / 100;
			}

			if (sourceType === "spins") {
				const raw = typeof element.triggerConditions === "string" ? JSON.parse(element.triggerConditions || "{}") : element.triggerConditions || {};

				const cond = raw.spins || {};
				providerId = cond.providerId;
				gameId = cond.gameId;
				spinsAmount = Number(cond.amount || 0);
			}

			if (element.rewardType === "freespins") {
				try {
					const parsed = typeof element.rewardConditions === "string" ? JSON.parse(element.rewardConditions || "{}") : element.rewardConditions || {};

					freespins = {
						providerId: Number(parsed.providerId) || null,
						gameId: Number(parsed.gameId) || null,
						betAmount: Number(parsed.betAmount) || 0,
						expiresAt: parsed.expiresAt || null,
						currency: parsed.currency || null,
					};
				} catch {
					freespins = null;
				}
			}

			const rewardType = element.rewardType;
			const rewardValue = rewardType === "chest" ? "" : rewardType === "money" ? element.rewardValue / 100 : element.rewardValue;

			const result = {
				...element,
				progress_required: element.count,
				rewardType,
				rewardValue,
			};

			if (trigger_type !== undefined) result.trigger_type = trigger_type;
			if (amount !== undefined) result.amount = amount;
			if (providerId !== undefined) result.providerId = providerId;
			if (gameId !== undefined) result.gameId = gameId;
			if (spinsAmount !== undefined) result.amount = spinsAmount;
			if (freespins) result.freespins = freespins;

			return result;
		});
	};

	loadAvailableGames(provider) {
		if (provider === "endorphina") {
			const gameState = this.props.game;
			if (!gameState?.loaded || !gameState?.data) {
				this.props.Interface.addNoty("Error", "Please reload page game state not loading");
				return;
			}
			const endorphinaGames = gameState.data.filter((g) => g.providerID === 10 && g.active === 1);
			const formatted = endorphinaGames.map((g) => ({
				id: g.gameID,
				description: g.name,
				name: g.name,
				gameID: g.gameID,
				providerID: g.providerID,
			}));
			this.props.setAchievementEditModal((prev) => ({ ...prev, availableGames: formatted }));
		}
	}

	getFilteredGames() {
		const games = this.props.achievementEditModal.availableGames || [];
		return games.map((g) => ({
			label: g.description,
			value: g.description,
			gameId: g.id,
			game: g,
		}));
	}

	// ======= Spins Games (general) =======
	loadAvailableSpinsGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const filteredGames = gameState.data.filter((game) => {
			return game.providerID === provider && game.active === 1;
		});

		const formattedGames = filteredGames.map((game) => ({
			id: game.gameID,
			description: game.name,
			name: game.name,
			gameID: game.gameID,
			providerID: game.providerID,
		}));

		this.props.setAchievementEditModal((prevState) => ({
			...prevState,
			availableSpinsGames: formattedGames,
		}));
	};

	getFilteredSpinsGames() {
		const games = this.props.achievementEditModal.availableSpinsGames || [];

		return games.map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));
	}

	// ======= Level Spins Games =======
	loadAvailableLevelSpinsGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const filteredGames = gameState.data.filter((game) => {
			return game.providerID === provider && game.active === 1;
		});

		const formattedGames = filteredGames.map((game) => ({
			id: game.gameID,
			description: game.name,
			name: game.name,
			gameID: game.gameID,
			providerID: game.providerID,
		}));

		this.setState({ availableLevelSpinsGames: formattedGames });
	};

	getFilteredLevelSpinsGames() {
		const games = this.state.availableLevelSpinsGames || [];

		return games.map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));
	}

	updateDailyProgressiveConfig = (patch) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				...patch,
			},
		}));
	};

	updateDailyConditions = (patch) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				dailyConditions: {
					...prev.dailyProgressiveConfig.dailyConditions,
					...patch,
				},
			},
		}));
	};

	updateDailyConditionsDeposit = (patch) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				dailyConditions: {
					...prev.dailyProgressiveConfig.dailyConditions,
					deposit: {
						...prev.dailyProgressiveConfig.dailyConditions.deposit,
						...patch,
					},
				},
			},
		}));
	};

	updateDailyConditionsSpins = (patch) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				dailyConditions: {
					...prev.dailyProgressiveConfig.dailyConditions,
					spins: {
						...prev.dailyProgressiveConfig.dailyConditions.spins,
						...patch,
					},
				},
			},
		}));
	};

	loadAvailableMilestoneGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const formattedGames = getGamesByProvider(gameState.data, provider);
		this.setState({ availableMilestoneGames: formattedGames });
	};

	getFilteredMilestoneGames = () => {
		return formatGamesForSelect(this.state.availableMilestoneGames || []);
	};

	addMilestone = () => {
		const { newMilestone, dailyProgressiveConfig } = this.state;

		if (!newMilestone.day || !newMilestone.rewardType) {
			this.props.Interface.addNoty("Error", "Day and reward type are required");
			return;
		}

		if (newMilestone.rewardType.value !== "chest" && (newMilestone.rewardValue === "" || newMilestone.rewardValue === null)) {
			this.props.Interface.addNoty("Error", "Reward value is required");
			return;
		}

		const milestone = {
			id: crypto.randomUUID(),
			day: parseInt(newMilestone.day, 10),
			rewardType: newMilestone.rewardType.value,
			rewardValue: newMilestone.rewardType.value === "chest" ? 1 : parseInt(newMilestone.rewardValue, 10) || 0,
			rewardConditions: {},
		};

		if (newMilestone.rewardType.value === "freespins") {
			if (!newMilestone.freespinsProvider || !newMilestone.freespinsGame) {
				this.props.Interface.addNoty("Error", "Provider and game are required for freespins");
				return;
			}
			milestone.rewardConditions = {
				gameId: newMilestone.freespinsGame.gameId,
				providerId: newMilestone.freespinsGame.game.providerID,
				betAmount: parseInt(newMilestone.freespinsBetAmount, 10) || 0,
				currency: "RUB",
				expiresAt: parseInt(newMilestone.freespinsExpiresAt, 10) || 7,
			};
		} else if (newMilestone.rewardType.value === "chest") {
			const chestValidation = this.validateSelectedChest({
				chestPrizeId: newMilestone.chestPrizeId,
			});
			if (chestValidation) {
				this.props.Interface.addNoty("Error", chestValidation);
				return;
			}
			milestone.rewardConditions = {
				chestPrizeId: Number(newMilestone.chestPrizeId),
			};
		}

		this.setState({
			dailyProgressiveConfig: {
				...dailyProgressiveConfig,
				milestones: [...dailyProgressiveConfig.milestones, milestone],
			},
			newMilestone: { ...INITIAL_NEW_MILESTONE },
			availableMilestoneGames: [],
		});
	};

	removeMilestone = (id) => {
		this.setState((prev) => ({
			dailyProgressiveConfig: {
				...prev.dailyProgressiveConfig,
				milestones: prev.dailyProgressiveConfig.milestones.filter((m) => m.id !== id),
			},
		}));
	};

	updateNewMilestone = (patch) => {
		this.setState((prev) => ({
			newMilestone: {
				...prev.newMilestone,
				...patch,
			},
		}));
	};

	loadDailyConditionsSpinsGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const formattedGames = getGamesByProvider(gameState.data, provider);
		this.setState({ dailyConditionsSpinsGames: formattedGames });
	};

	getFilteredDailyConditionsSpinsGames = () => {
		return formatGamesForSelect(this.state.dailyConditionsSpinsGames || []);
	};

	renderComponentFromType(groupId, type, specialType) {
		const newCondition = this.state.newConditionByGroupId?.[groupId] || {};

		if (type === "multiselect" && specialType === "riskModel") {
			return <Multiselect selectedOptions={getRiskModelSelectedOptions(newCondition?.value)} onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOptions.map((option) => option.value) })} options={RISK_MODEL_OPTIONS} placeholder="Select risk models" selectedAriaLabel="Selected" />;
		} else if (type === "string") {
			return <Input placeholder="Enter value" value={newCondition?.value || ""} onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value })} />;
		} else if (type === "count") {
			return <Input placeholder="Enter value" type="number" value={newCondition?.value || ""} onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value })} />;
		} else if (type === "date") {
			return <DatePicker onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.value })} value={newCondition?.value || null} placeholder="Select a date" />;
		} else if (type === "select" && specialType === "boolean") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={[
						{ value: "1", label: "Required" },
						{ value: "0", label: "No" },
					]}
					placeholder="Select type"
				/>
			);
		} else if (type === "select" && specialType === "cryptoOrFiat") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={[
						{ value: "crypto", label: "Crypto" },
						{ value: "fiat", label: "Fiat" },
					]}
					placeholder="Select type"
				/>
			);
		} else if (type === "select" && specialType === "partnerPaymentType") {
			return (
				<Select
					selectedOption={newCondition?.value}
					onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOption })}
					options={PARTNER_PAYMENT_TYPE_OPTIONS}
					placeholder="Select payment type"
				/>
			);
		}

		return null;
	}

	// ======= Image =======
	handleImageChange = (event) => {
		const file = event.target.files[0];
		this.setState({ imageError: "" });

		if (file && (file.type === "image/svg+xml" || file.type === "image/png")) {
			if (this.props.achievementEditModal.image) {
				URL.revokeObjectURL(this.props.achievementEditModal.image);
			}
			const imageUrl = URL.createObjectURL(file);
			this.setState({ jsonImagePreview: "" });
			this.props.setAchievementEditModal({
				...this.props.achievementEditModal,
				image: imageUrl,
				imageFile: file,
			});
		} else {
			this.setState({ imageError: "Please select a valid SVG or PNG file" });
			this.props.setAchievementEditModal({
				...this.props.achievementEditModal,
				image: "",
				imageFile: null,
			});
		}
	};

	fileToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
			reader.readAsDataURL(file);
		});
	};

	getAvailableEndorphinaGames = () => {
		const gameState = this.props.game;
		if (!gameState?.loaded || !gameState?.data) {
			return [];
		}

		return gameState.data
			.filter((game) => game.providerID === 10 && game.active === 1)
			.map((game) => ({
				id: game.gameID,
				description: game.name,
				name: game.name,
				gameID: game.gameID,
				providerID: game.providerID,
			}));
	};

	getChestRewardGames = () =>
		this.getAvailableEndorphinaGames().map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));

	parseRewardConditions = (rewardConditions) => {
		if (!rewardConditions) {
			return {};
		}

		if (typeof rewardConditions === "string") {
			try {
				return JSON.parse(rewardConditions);
			} catch (e) {
				return {};
			}
		}

		return rewardConditions;
	};

	buildFreespinRewardConditions = ({ gameSelected, betAmount, expiresAt }) => {
		const parsedBetAmount = parseFloat(betAmount) || 0;

		return {
			gameId: gameSelected?.gameId,
			providerId: gameSelected?.game?.providerID,
			betAmount: parsedBetAmount,
			currency: "RUB",
			expiresAt: parseInt(expiresAt, 10),
		};
	};

	buildChestRewardsPayload = (items = this.props.achievementEditModal.chestRewards || []) =>
		(items || []).map((item) => {
			const rewardType = item.rewardType?.value || item.rewardType;

			return {
				rewardType,
				rewardValue: parseInt(item.rewardValue, 10) || 0,
				probability: Number(item.probability),
				rewardConditions:
					rewardType === "freespins"
						? this.buildFreespinRewardConditions(item)
						: item.rewardConditions || {},
			};
		});

	loadChestPrizeOptions = async ({
		matchCurrent = false,
		currentType = "",
		currentRewardConditions = [],
	} = {}) => {
		try {
			this.props.setAchievementEditModal((prev) => ({
				...prev,
				chestPrizeLoading: true,
			}));

			const response = await this.props.Interface.getChestPrizeOptions({ page: 1, limit: 999 });
			const items = response?.items || [];
			const options = items.map((item) => buildChestPrizeOption(item));
			let patch = {
				chestPrizeOptions: options,
				chestPrizeLoading: false,
			};

			if (matchCurrent) {
				const match = findMatchingChestPrize(items, {
					type: currentType,
					rewardConditions: currentRewardConditions,
				});

				patch = {
					...patch,
					chestPrizeSelected: match ? buildChestPrizeOption(match) : null,
					chestPrizeMatchMissing: !match && Boolean(currentType || (currentRewardConditions || []).length),
				};
			}

			this.props.setAchievementEditModal((prev) => ({
				...prev,
				...patch,
			}));

			return { items, options };
		} catch (error) {
			console.error(error);
			this.props.setAchievementEditModal((prev) => ({
				...prev,
				chestPrizeLoading: false,
				chestPrizeMatchMissing:
					matchCurrent && Boolean(currentType || (currentRewardConditions || []).length),
			}));
			this.props.Interface.addNoty("Error", "Failed to load chest presets");
			return { items: [], options: [] };
		}
	};

	selectChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.props.setAchievementEditModal({
			...this.props.achievementEditModal,
			chestPrizeSelected: selectedOption,
			chestPrizeId: chestPrize?.id || null,
			chestType: chestPrize?.type || "",
			chestRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			chestPrizeMatchMissing: false,
		});
	};

	selectNewLevelChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.setState({
			newChestPrizeSelected: selectedOption,
			newChestPrizeId: chestPrize?.id || null,
			newChestType: chestPrize?.type || "",
			newChestRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			levelError: "",
		});
	};

	selectDailyMilestoneChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.setState((prev) => ({
			newMilestone: {
				...prev.newMilestone,
				chestPrizeSelected: selectedOption,
				chestPrizeId: chestPrize?.id || null,
				chestType: chestPrize?.type || "",
				chestRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			},
		}));
	};

	selectEditingChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.setState({
			chestEditingPrizeSelected: selectedOption,
			chestEditingPrizeId: chestPrize?.id || null,
			chestEditingType: chestPrize?.type || "",
			chestEditingRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			chestEditingMatchMissing: false,
			levelError: "",
		});
	};

	validateSelectedChest = ({ chestPrizeId }) => {
		if (!chestPrizeId) {
			return "Chest preset is required";
		}

		return "";
	};

	validateChestRewards = (chestRewards = []) => {
		if (!chestRewards.length) return "Chest rewards are required";

		for (let index = 0; index < chestRewards.length; index += 1) {
			const reward = chestRewards[index];
			const rewardLabel = `Chest reward #${index + 1}`;
			const rewardType = reward.rewardType?.value || reward.rewardType;

			if (!rewardType) return `${rewardLabel}: reward type is required`;

			const rewardValue = parseInt(reward.rewardValue, 10);
			if (!reward.rewardValue || rewardValue < 1) {
				return `${rewardLabel}: reward value is required`;
			}

			const probability = Number(reward.probability || 0);
			if (Number.isNaN(probability) || probability < 0) {
				return `${rewardLabel}: probability must not be negative`;
			}

			if (rewardType === "freespins") {
				if (!reward.providerSelected) return `${rewardLabel}: provider is required`;
				if (!reward.gameSelected) return `${rewardLabel}: game is required`;
				if (!reward.betAmount) return `${rewardLabel}: bet amount is required`;
				if (!reward.expiresAt) return `${rewardLabel}: expiresAt is required`;

				const betAmount = parseFloat(reward.betAmount) || 0;
				if (betAmount <= 0) return `${rewardLabel}: bet amount must be greater than 0`;
			}
		}

		return "";
	};

	getChestRewardsSummary = (rewardConditions) => {
		const parsed = this.parseRewardConditions(rewardConditions);
		const prizes = parsed?.prizes || [];
		if (!prizes.length) {
			return "Chest config";
		}

		const totalProbability = prizes.reduce(
			(sum, reward) => sum + (Number(reward.probability) || 0),
			0
		);

		return `Chest • ${prizes.length} rewards • ${totalProbability}%`;
	};

	getChestPrizeItems = () =>
		(this.props.achievementEditModal.chestPrizeOptions || [])
			.map((option) => option.chestPrize)
			.filter(Boolean);

	getChestDisplayLabel = (rewardConditions) =>
		getChestPrizeDisplayLabel(this.getChestPrizeItems(), {
			type: getChestTypeFromRewardConditions(rewardConditions),
			rewardConditions,
			fallback: this.getChestRewardsSummary(rewardConditions),
		});

	hydrateChestRewards = (prizes) =>
		(prizes || []).map((item) => {
			const rewardConditions = this.parseRewardConditions(item.rewardConditions);
			const rewardType = item.rewardType;
			const providerSelected = rewardType === "freespins" ? FREESPINS_PROVIDER_OPTIONS[0] : null;
			let gameSelected = null;

			if (rewardType === "freespins" && rewardConditions?.gameId) {
				const availableGames = this.getAvailableEndorphinaGames();
				const foundGame = availableGames.find((game) => game.id === Number(rewardConditions.gameId));
				if (foundGame) {
					gameSelected = {
						label: foundGame.description,
						value: foundGame.description,
						gameId: foundGame.id,
						game: foundGame,
					};
				}
			}

			return {
				...createChestRewardDraft(),
				rewardType: CHEST_REWARD_OPTIONS.find((option) => option.value === rewardType) || null,
				rewardValue: item.rewardValue != null ? String(item.rewardValue) : "",
				probability: item.probability != null ? String(item.probability) : "",
				rewardConditions,
				providerSelected,
				gameSelected,
				betAmount: rewardConditions?.betAmount != null ? String(rewardConditions.betAmount) : "",
				expiresAt: rewardConditions?.expiresAt != null ? String(rewardConditions.expiresAt) : "",
			};
		});

	addChestReward = () => {
		this.props.setAchievementEditModal((prev) => ({
			...prev,
			chestRewards: [...(prev.chestRewards || []), createChestRewardDraft()],
		}));
	};

	removeChestReward = (rewardId) => {
		this.props.setAchievementEditModal((prev) => ({
			...prev,
			chestRewards: (prev.chestRewards || []).filter((item) => item.id !== rewardId),
		}));
	};

	updateChestReward = (rewardId, patch) => {
		this.props.setAchievementEditModal((prev) => ({
			...prev,
			chestRewards: (prev.chestRewards || []).map((item) =>
				item.id === rewardId ? { ...item, ...patch } : item
			),
		}));
	};

	handleChestRewardTypeChange = (rewardId, rewardType) => {
		this.updateChestReward(rewardId, {
			rewardType,
			rewardValue: "",
			probability: "",
			rewardConditions: {},
			providerSelected: null,
			gameSelected: null,
			betAmount: "",
			expiresAt: "",
		});
	};

	addNewLevelChestReward = () => {
		this.setState((prev) => ({
			newChestRewards: [...(prev.newChestRewards || []), createChestRewardDraft()],
		}));
	};

	removeNewLevelChestReward = (rewardId) => {
		this.setState((prev) => ({
			newChestRewards: (prev.newChestRewards || []).filter((item) => item.id !== rewardId),
		}));
	};

	updateNewLevelChestReward = (rewardId, patch) => {
		this.setState((prev) => ({
			newChestRewards: (prev.newChestRewards || []).map((item) =>
				item.id === rewardId ? { ...item, ...patch } : item
			),
		}));
	};

	handleNewLevelChestRewardTypeChange = (rewardId, rewardType) => {
		this.updateNewLevelChestReward(rewardId, {
			rewardType,
			rewardValue: "",
			probability: "",
			rewardConditions: {},
			providerSelected: null,
			gameSelected: null,
			betAmount: "",
			expiresAt: "",
		});
	};

	addEditingChestReward = () => {
		this.setState((prev) => ({
			chestEditingRewards: [...(prev.chestEditingRewards || []), createChestRewardDraft()],
		}));
	};

	removeEditingChestReward = (rewardId) => {
		this.setState((prev) => ({
			chestEditingRewards: (prev.chestEditingRewards || []).filter((item) => item.id !== rewardId),
		}));
	};

	updateEditingChestReward = (rewardId, patch) => {
		this.setState((prev) => ({
			chestEditingRewards: (prev.chestEditingRewards || []).map((item) =>
				item.id === rewardId ? { ...item, ...patch } : item
			),
		}));
	};

	handleEditingChestRewardTypeChange = (rewardId, rewardType) => {
		this.updateEditingChestReward(rewardId, {
			rewardType,
			rewardValue: "",
			probability: "",
			rewardConditions: {},
			providerSelected: null,
			gameSelected: null,
			betAmount: "",
			expiresAt: "",
		});
	};

	buildRewardConditions = (modalState) => {
		if (modalState.rewardType?.value === "chest") {
			return {
				chestPrizeId: Number(modalState.chestPrizeId),
			};
		}

		if (modalState.rewardType?.value === "freespins") {
			return this.buildFreespinRewardConditions({
				gameSelected: modalState.gameSelected,
				betAmount: modalState.betAmount,
				expiresAt: modalState.expiresAt,
			});
		}

		return {};
	};

	getImageExtensionFromPath = (image) => {
		const extension = String(image || "").split("?")[0].split(".").pop();
		return extension === "png" ? "png" : "svg";
	};

	buildTransferData = () => buildAchievementTransferData(this.props.achievementEditModal, this.state.dailyProgressiveConfig);

	getCurrentJsonText = (validate = false) => {
		const data = this.buildTransferData();
		return validate ? getAchievementTransferJson(data) : stringifyAchievementTransfer(data);
	};

	getJsonTextareaValue = () => {
		if (this.state.jsonDirty) return this.state.jsonText;
		try {
			return this.getCurrentJsonText();
		} catch {
			return this.state.jsonText;
		}
	};

	copyAchievementJson = async () => {
		try {
			const jsonText = this.getCurrentJsonText(true);
			this.setState({ jsonText, jsonError: "", jsonDirty: false });
			await copyTextToClipboard(jsonText);
			this.props.Interface.addNoty("Success", "Achievement JSON copied");
		} catch (error) {
			this.setState({ jsonError: error.message || "Failed to copy JSON" });
			this.props.Interface.addNoty("Error", "Failed to copy achievement JSON");
		}
	};

	applyAchievementJson = () => {
		try {
			const data = parseAchievementTransferEnvelope(this.getJsonTextareaValue());
			const changedLockedFields = findChangedLockedAchievementFields(this.buildTransferData(), data);
			if (changedLockedFields.length) {
				throw new Error(`Achievement JSON has read-only changes: ${changedLockedFields.join(", ")}`);
			}
			this.props.setAchievementEditModal(buildEditAchievementStateFromTransfer(this.props.achievementEditModal, data));
			this.setState({ jsonError: "", jsonDirty: false, jsonImagePreview: data.image || "" });
			this.props.Interface.addNoty("Success", "Editable achievement fields applied from JSON");
		} catch (error) {
			this.setState({ jsonError: error.message || "Invalid achievement JSON" });
			this.props.Interface.addNoty("Error", error.message || "Invalid achievement JSON");
		}
	};

	renderJsonImagePreview = () => {
		if (!this.state.jsonImagePreview) return null;

		return (
			<Alert type="info" header="JSON image preview">
				<SpaceBetween direction="vertical" size="s">
					<Box>Image from JSON is preview only. Upload the image manually for the current project CDN before saving.</Box>
					<Box textAlign="center">
						<img src={this.state.jsonImagePreview} alt="JSON achievement preview" style={{ maxWidth: "100%", maxHeight: "150px", padding: "4px" }} />
					</Box>
				</SpaceBetween>
			</Alert>
		);
	};

	validation(payload) {
		if (!payload.name) return "Achievement name is required";
		if (!payload.description) return "Description is required";
		if (!payload.nameTranslationType) return "Name translation type is required";
		if (!payload.descriptionTranslationType) return "Description translation type is required";
		if (!payload.buttonTranslationType) return "Button translation type is required";
		if (!payload.buttonText) return "Button text is required";
		if (!payload.link) return "Link is required";
		if (Number.isNaN(payload.active)) return "Active status is required";
		if (Number.isNaN(payload.order)) return "Order is required";

		return "";
	}

	validateDateRange(cycle, startDate, endDate) {
		if (cycle?.value !== "not") return "";
		if (!startDate || !endDate) return "Achievement date range is required";
		if (Number.isNaN(new Date(startDate).getTime()) || Number.isNaN(new Date(endDate).getTime())) {
			return "Achievement date range is invalid";
		}
		if (new Date(startDate) > new Date(endDate)) {
			return "Achievement date range is invalid. The start date must be before the end date.";
		}
		return "";
	}

	loadAvailableFreespinsGames = (provider) => {
		const gameState = this.props.game;

		if (!gameState?.loaded || !gameState?.data) {
			this.props.Interface.addNoty("Error", "Please reload page game state not loading");
			return;
		}

		const filteredGames = gameState.data.filter((game) => {
			return game.providerID === provider && game.active === 1;
		});

		const formattedGames = filteredGames.map((game) => ({
			id: game.gameID,
			description: game.name,
			name: game.name,
			gameID: game.gameID,
			providerID: game.providerID,
		}));

		this.props.setAchievementEditModal((prevState) => ({
			...prevState,
			availableFreespinsGames: formattedGames,
		}));
	};

	validateNewLevel = () => {
		const t = this.state?.newRewardType?.value;
		if (!this.state?.newLevel) return "Level is required";
		if (!this.state?.newProgress && this.state?.newProgress !== 0) return "Count is required";
		if (!t) return "Reward type is required";

		const A = this.props.achievementEditModal;
		if (A.sourceType?.value === "deposit" || A.sourceType?.value === "withdrawal") {
			if (this.state?.newAmount === undefined || this.state?.newAmount === "") return "Amount is required for deposit/withdrawal";
			if (!this.state?.newTriggerType) return "Trigger type is required for deposit/withdrawal";
		}

		if (t === "freespins") {
			const spins = Number(this.state?.newRewardValue || 0);
			if (!this.state?.newProvider) return "Provider is required for freespins";
			if (!this.state?.newGame) return "Game is required for freespins";
			if (!this.state?.newBetAmount || Number(this.state?.newBetAmount) <= 0) return "Bet amount must be > 0";
			if (!this.state?.newExpiresAt) return "ExpiresAt is required for freespins";
			if (spins < 1 || spins > 1000) return "Reward Value (spins) must be 1-1000";
		} else if (t === "chest") {
			return this.validateSelectedChest({
				chestPrizeId: this.state?.newChestPrizeId,
			});
		} else {
			if (this.state?.newRewardValue === undefined || this.state?.newRewardValue === "") {
				return "Reward value is required";
			}
		}
		return "";
	};

	renderChestRewardsEditor = ({ chestRewards = [], onAdd, onRemove, onUpdate, onTypeChange }) => (
		<Container header={<Box variant="h4">Chest configuration</Box>}>
			<SpaceBetween direction="vertical" size="m">
				<Button onClick={onAdd}>Add chest reward</Button>

				{!chestRewards.length && (
					<Alert type="info">Add at least one reward to the chest pool.</Alert>
				)}

				{chestRewards.map((reward, index) => {
					const rewardType = reward.rewardType?.value || reward.rewardType;

					return (
						<Container key={reward.id} header={<Box variant="h4">{`Reward #${index + 1}`}</Box>}>
							<SpaceBetween direction="vertical" size="m">
								<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
									<FormField label="Reward type" stretch>
										<Select
											selectedOption={reward.rewardType}
											options={CHEST_REWARD_OPTIONS}
											placeholder="Select reward type"
											onChange={({ detail }) => onTypeChange(reward.id, detail.selectedOption)}
										/>
									</FormField>

									<FormField label="Reward value" stretch>
										<Input
											type="number"
											placeholder="Enter reward value"
											value={reward.rewardValue}
											onChange={(e) => onUpdate(reward.id, { rewardValue: e.detail.value })}
										/>
									</FormField>

									<FormField label="Probability" stretch>
										<Input
											type="number"
											inputMode="decimal"
											placeholder="Enter probability"
											value={reward.probability}
											onChange={(e) => onUpdate(reward.id, { probability: e.detail.value })}
										/>
									</FormField>
								</Grid>

								{rewardType === "freespins" && (
									<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
										<FormField label="Provider" stretch>
											<Select
												selectedOption={reward.providerSelected}
												options={FREESPINS_PROVIDER_OPTIONS}
												placeholder="Select provider"
												onChange={({ detail }) =>
													onUpdate(reward.id, {
														providerSelected: detail.selectedOption,
														gameSelected: null,
													})
												}
											/>
										</FormField>

										{reward.providerSelected && (
											<>
												<FormField label="Game" stretch>
													<Select
														filteringType="auto"
														selectedOption={reward.gameSelected}
														options={this.getChestRewardGames()}
														placeholder="Select game"
														onChange={({ detail }) =>
															onUpdate(reward.id, { gameSelected: detail.selectedOption })
														}
													/>
												</FormField>

												<FormField label="Bet amount" stretch>
													<Input
														type="number"
														inputMode="decimal"
														placeholder="Enter bet amount"
														value={reward.betAmount || ""}
														onChange={(e) => onUpdate(reward.id, { betAmount: e.detail.value })}
													/>
												</FormField>

												<FormField label="Expiration date" stretch>
													<Input
														placeholder="Enter expiration date"
														value={reward.expiresAt || ""}
														onChange={(e) => onUpdate(reward.id, { expiresAt: e.detail.value })}
													/>
												</FormField>
											</>
										)}
									</Grid>
								)}

								<Button variant="link" onClick={() => onRemove(reward.id)}>
									Remove reward
								</Button>
							</SpaceBetween>
						</Container>
					);
				})}

				{chestRewards.length > 0 && (
					<Box variant="p" color="text-status-info">
						{`Total probability: ${chestRewards.reduce(
							(sum, item) => sum + (Number(item.probability) || 0),
							0
						)}`}
					</Box>
				)}
			</SpaceBetween>
		</Container>
	);

	renderNewLevelChestConfig = () => {
		return (
			<Container header={<Box variant="h4">Chest preset</Box>}>
				<SpaceBetween direction="vertical" size="m">
					<FormField label="Chest" stretch>
						<Select
							filteringType="auto"
							placeholder="Select chest preset"
							selectedOption={this.state.newChestPrizeSelected}
							onChange={({ detail }) => this.selectNewLevelChestPrize(detail.selectedOption)}
							options={this.props.achievementEditModal.chestPrizeOptions}
							statusType={this.props.achievementEditModal.chestPrizeLoading ? "loading" : "finished"}
							loadingText="Loading chest presets"
							empty="No chest presets found"
						/>
					</FormField>

					{this.state.newChestType && (
						<Box>{`Chest type: ${formatChestPrizeType(this.state.newChestType)}`}</Box>
					)}

					{(this.state.newChestRewards || []).length > 0 && (
						<Box>{`Rewards in chest: ${(this.state.newChestRewards || []).length}`}</Box>
					)}
				</SpaceBetween>
			</Container>
		);
	};

	validateJsonBeforeSave = () => {
		if (!this.state.jsonDirty) return "";

		try {
			const data = parseAchievementTransferEnvelope(this.getJsonTextareaValue());
			const changedLockedFields = findChangedLockedAchievementFields(this.buildTransferData(), data);
			if (changedLockedFields.length) {
				return `Achievement JSON has read-only changes: ${changedLockedFields.join(", ")}`;
			}
			return "";
		} catch (error) {
			return error.message || "Invalid achievement JSON";
		}
	};

	// ======= Submit =======
	addAchievement = async () => {
		try {
			const A = this.props.achievementEditModal;
			this.props.setAchievementEditModal({ ...A, loaded: false });

			let imageBase64 = "";
			let imageExtension = "";
			if (A.imageFile) {
				try {
					imageBase64 = await this.fileToBase64(A.imageFile);
					imageExtension = A.imageFile.type === "image/png" ? "png" : "svg";
				} catch (error) {
					console.error("Failed to convert image to base64:", error);
					this.props.Interface.addNoty("Error", "Failed to process image");
					this.props.setAchievementEditModal({ ...A, loaded: true });
					return;
				}
			} else if (typeof A.image === "string" && A.image.length > 0) {
				imageBase64 = A.image;
				imageExtension = this.getImageExtensionFromPath(A.image);
			}

			const finalPayload = {
				id: A.id,
				name: A.name,
				description: A.description,
				nameTranslationType: A.nameTranslationType,
				descriptionTranslationType: A.descriptionTranslationType,
				buttonTranslationType: A.buttonTranslationType,
				image: imageBase64,
				image_extension: imageExtension.includes("png") ? "png" : "svg",
				link: A.link,
				active: parseInt(A.active.value),
				buttonText: A.buttonText,
				order: parseInt(A.order),
			};

			if (A.rewardType?.value === "chest" && A.type?.value !== "progressive" && A.type?.value !== "daily_progressive") {
				const chestValidation = this.validateSelectedChest({ chestPrizeId: A.chestPrizeId });
				if (chestValidation.length) {
					this.props.Interface.addNoty("Error", chestValidation);
					this.props.setAchievementEditModal({ ...A, loaded: true });
					return;
				}

				finalPayload.rewardConditions = {
					chestPrizeId: Number(A.chestPrizeId),
				};
			}

			if (A.cycle?.value === "not") {
				finalPayload.startDate = A.startDate;
				finalPayload.endDate = A.endDate;
			}

			const validation = this.validation(finalPayload);
			if (validation.length) {
				this.props.Interface.addNoty("Error", validation);
				this.props.setAchievementEditModal({ ...A, loaded: true });
				return;
			}

			const dateValidation = this.validateDateRange(A.cycle, A.startDate, A.endDate);
			if (dateValidation.length) {
				this.props.Interface.addNoty("Error", dateValidation);
				this.props.setAchievementEditModal({ ...A, loaded: true });
				return;
			}

			const jsonValidation = this.validateJsonBeforeSave();
			if (jsonValidation.length) {
				this.props.Interface.addNoty("Error", jsonValidation);
				this.props.setAchievementEditModal({ ...A, loaded: true });
				return;
			}

			this.props.Interface.request("set", { class: "data", module: "achievement", method: "edit" }, finalPayload)
				.then(() => {
					this.props.Interface.addNoty("Success", `Achievement "${A.name}" was successfully edited`);

					if (A.image) URL.revokeObjectURL(A.image);

					this.props.setAchievementEditModal({
						show: false,
						loaded: true,
						name: "",
						description: "",
						sourceType: null,
						type: "",
						reward_tickets: "",
						rewardType: null,
						rewardValue: "",
						image: "",
						imageFile: null,
						link: "",
						count: "",
						active: false,
						buttonText: "",
						cycle: null,
						cycleUnit: null,
						cycleTimer: null,
						order: "",
						conditions: [],
						levels: [],
						availableGames: [],
						providerSelected: null,
						gameSelected: null,
						betAmount: "",
						expiresAt: "",
						chestRewards: [],
						chestType: "",
						chestPrizeId: null,
						chestPrizeSelected: null,
						chestPrizeOptions: [],
						chestPrizeLoading: false,
						chestPrizeMatchMissing: false,
						startDate: "",
						endDate: "",
						providerSpinsSelected: null,
						gameSpinsSelected: null,
						availableSpinsGames: [],
					});
					this.setState({
						imageError: "",
						levelError: "",
						fsEditingRow: null,
						fsEditing: null,
						chestEditingRow: null,
						chestEditingRewards: [],
						chestEditingPrizeId: null,
						chestEditingPrizeSelected: null,
						chestEditingType: "",
						chestEditingMatchMissing: false,
						spinsEditingRow: null,
						spinsEditing: null,
						availableLevelSpinsGames: [],
						newSpinsProvider: null,
						newSpinsGame: null,
						newSpinsAmount: null,
						newChestRewards: [],
						newChestType: "",
						newChestPrizeId: null,
						newChestPrizeSelected: null,
						jsonText: "",
						jsonError: "",
						jsonDirty: false,
						jsonImagePreview: "",
					});
					this.props.Interface.loadAchievementList();
				})
				.catch((error) => {
					console.error("Failed to edit achievement:", error);
					this.props.setAchievementEditModal({ ...A, loaded: true });
					this.props.Interface.addNoty("Error", "Failed to edit achievement");
				});
		} catch (e) {
			console.log(e);
			this.props.Interface.addNoty("Error", e?.text || "Unknown error");
			this.props.setAchievementEditModal({ ...this.props.achievementEditModal, loaded: true });
		}
	};

	openFsModal = async (item) => {
		const providerSelected = item.freespins?.providerId ? { value: 10, label: "Endorphina" } : null;

		let gameSelected = null;
		let availableGames = this.props.achievementEditModal.availableGames || [];

		if (providerSelected?.value && item.freespins?.gameId) {
			if (providerSelected.value === 10) {
				const gameState = this.props.game;
				if (!gameState?.loaded || !gameState?.data) {
					this.props.Interface.addNoty("Error", "Please reload page game state not loading");
					return;
				}
				const endorphinaGames = gameState.data.filter((g) => g.providerID === 10 && g.active === 1);
				availableGames = endorphinaGames.map((g) => ({
					id: g.gameID,
					description: g.name,
					name: g.name,
					gameID: g.gameID,
					providerID: g.providerID,
				}));

				this.props.setAchievementEditModal((prev) => ({ ...prev, availableGames }));
			}

			const foundGame = availableGames.find((game) => game.id === Number(item.freespins.gameId));
			if (foundGame) {
				gameSelected = {
					label: foundGame.description,
					value: foundGame.description,
					gameId: foundGame.id,
					game: foundGame,
				};
			}
		}

		this.setState({
			fsEditingRow: item.id ?? item.deleteId,
			fsEditing: {
				providerSelected,
				gameSelected,
				betAmount: item?.freespins?.betAmount ?? "",
				expiresAt: item?.freespins?.expiresAt ?? "",
			},
		});
	};

	closeFsModal = () => {
		this.setState({ fsEditingRow: null, fsEditing: null, levelError: "" });
	};

	validateFsModal = () => {
		const fs = this.state.fsEditing || {};
		if (!fs.providerSelected) return "Provider is required for freespins";
		if (!fs.gameSelected) return "Game is required for freespins";
		const bet = Number(fs.betAmount || 0);
		if (!(bet > 0)) return "Bet amount must be greater than 0";
		if (fs.expiresAt === undefined || fs.expiresAt === "") return "ExpiresAt is required for freespins";
		return "";
	};

	saveFsModal = () => {
		const err = this.validateFsModal();
		if (err) {
			this.setState({ levelError: err });
			return;
		}
		const rowId = this.state.fsEditingRow;
		const { providerSelected, gameSelected, betAmount, expiresAt } = this.state.fsEditing;

		const newLvls = (this.props.achievementEditModal.levels || []).map((el) => {
			if (typeof el.deleteId !== "undefined" ? el.deleteId !== rowId : el.id !== rowId) return el;
			return {
				...el,
				rewardType: "freespins",
				freespins: {
					providerId: providerSelected?.type || 10,
					gameId: gameSelected?.game.id,
					betAmount: Number(betAmount) || 0,
					expiresAt: expiresAt,
					currency: "RUB",
				},
			};
		});

		this.props.setAchievementEditModal({ ...this.props.achievementEditModal, levels: newLvls });
		this.setState({ fsEditingRow: null, fsEditing: null, levelError: "" });
	};

	openChestModal = async (item) => {
		const rewardConditions = this.parseRewardConditions(item.rewardConditions);
		const chestType = getChestTypeFromRewardConditions(rewardConditions);
		const chestRewards = getChestRewardsPayload(rewardConditions);
		const chestPrizeId = getChestPrizeIdFromRewardConditions(rewardConditions);

		this.setState({
			chestEditingRow: item.id ?? item.deleteId,
			chestEditingRewards: chestRewards,
			chestEditingType: chestType,
			chestEditingPrizeId: chestPrizeId,
			chestEditingPrizeSelected: null,
			chestEditingMatchMissing: false,
			levelError: "",
		});

		const { items } = await this.loadChestPrizeOptions();
		const match = findMatchingChestPrize(items, {
			type: chestType,
			rewardConditions,
		});

		this.setState({
			chestEditingPrizeSelected: match ? buildChestPrizeOption(match) : null,
			chestEditingPrizeId: match?.id || chestPrizeId,
			chestEditingMatchMissing: !match && Boolean(chestType || chestRewards.length),
		});
	};

	closeChestModal = () => {
		this.setState({
			chestEditingRow: null,
			chestEditingRewards: [],
			chestEditingType: "",
			chestEditingPrizeId: null,
			chestEditingPrizeSelected: null,
			chestEditingMatchMissing: false,
			levelError: "",
		});
	};

	saveChestModal = () => {
		const chestEditingRewards = this.state.chestEditingRewards || [];
		const err = this.validateSelectedChest({
			chestPrizeId: this.state.chestEditingPrizeId,
		});
		if (err) {
			this.setState({ levelError: err });
			return;
		}

		const rowId = this.state.chestEditingRow;
		const newLvls = (this.props.achievementEditModal.levels || []).map((el) => {
			if (typeof el.deleteId !== "undefined" ? el.deleteId !== rowId : el.id !== rowId) return el;

			const next = {
				...el,
				rewardType: "chest",
				rewardValue: 1,
				rewardConditions: {
					chestPrizeId: Number(this.state.chestEditingPrizeId),
				},
			};

			delete next.freespins;
			delete next.tickets_reward;

			return next;
		});

		this.props.setAchievementEditModal({ ...this.props.achievementEditModal, levels: newLvls });
		this.setState({
			chestEditingRow: null,
			chestEditingRewards: [],
			chestEditingType: "",
			chestEditingPrizeId: null,
			chestEditingPrizeSelected: null,
			chestEditingMatchMissing: false,
			levelError: "",
		});
	};

	openSpinsModal = (item) => {
		const providers = this.props.config.game.options.provider || [];
		const providerSelected = providers.find((p) => p.type === item.providerId) || null;

		let gameSelected = null;
		let availableGames = [];

		if (providerSelected) {
			const gameState = this.props.game;
			if (gameState?.loaded && gameState?.data) {
				const filteredGames = gameState.data.filter((g) => g.providerID === providerSelected.type && g.active === 1);
				availableGames = filteredGames.map((g) => ({
					id: g.gameID,
					description: g.name,
					name: g.name,
					gameID: g.gameID,
					providerID: g.providerID,
				}));

				if (item.gameId) {
					const foundGame = availableGames.find((g) => g.id === Number(item.gameId));
					if (foundGame) {
						gameSelected = {
							label: foundGame.description,
							value: foundGame.description,
							gameId: foundGame.id,
							game: foundGame,
						};
					}
				}
			}
		}

		this.setState({
			spinsEditingRow: item.id || item.deleteId,
			spinsEditing: {
				providerSelected,
				gameSelected,
				availableGames,
				amount: item.amount,
			},
		});
	};

	closeSpinsModal = () => {
		this.setState({ spinsEditingRow: null, spinsEditing: null, levelError: "" });
	};

	validateSpinsModal = () => {
		const s = this.state.spinsEditing || {};
		return "";
	};

	getFilteredFreespinsGames() {
		const games = this.props.achievementEditModal.availableFreespinsGames || [];

		return games.map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));
	}

	saveSpinsModal = () => {
		const err = this.validateSpinsModal();
		if (err) {
			this.setState({ levelError: err });
			return;
		}
		const rowId = this.state.spinsEditingRow;
		const { providerSelected, gameSelected, amount } = this.state.spinsEditing;

		const newLvls = (this.props.achievementEditModal.levels || []).map((el) => {
			if (el.id !== rowId) return el;
			return {
				...el,
				providerId: providerSelected?.type,
				gameId: gameSelected?.game?.id,
				amount,
			};
		});

		this.props.setAchievementEditModal({ ...this.props.achievementEditModal, levels: newLvls });
		this.setState({ spinsEditingRow: null, spinsEditing: null, levelError: "" });
	};

	// ======= Dismiss =======
	onDismiss = () => {
		this.setState({
			newChestRewards: [],
			newChestType: "",
			newChestPrizeId: null,
			newChestPrizeSelected: null,
			chestEditingRow: null,
			chestEditingRewards: [],
			chestEditingPrizeId: null,
			chestEditingPrizeSelected: null,
			chestEditingType: "",
			chestEditingMatchMissing: false,
			levelError: "",
			jsonText: "",
			jsonError: "",
			jsonDirty: false,
			jsonImagePreview: "",
		});
		this.props.setAchievementEditModal({ show: false });
	};

	render() {
		const A = this.props.achievementEditModal;

		const {
			fsEditingRow,
			fsEditing,
			chestEditingRow,
			chestEditingRewards,
			chestEditingPrizeSelected,
			chestEditingType,
			chestEditingMatchMissing,
			spinsEditingRow,
			spinsEditing,
		} = this.state;
		const typeValue = A.sourceType?.value;
		const lockedSectionStyle = {
			opacity: 0.5,
			pointerEvents: "none",
			filter: "grayscale(0.15)",
		};
		const logicLockMessage = "Business logic fields are read-only in edit mode. For regular chest achievements, only the chest preset can be changed.";

		return (
			<Modal
				visible={A.show}
				onDismiss={this.onDismiss}
				header={`Edit Achievement (${A.id})`}
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={this.onDismiss}>
								Cancel
							</Button>
							<Button variant="primary" iconName="check" onClick={this.addAchievement} loading={!A.loaded}>
								Edit Achievement
							</Button>
						</SpaceBetween>
					</Box>
				}>
				{A.loaded ? (
					<Form>
						{this.state.imageError && (
							<Alert type="error" dismissible onDismiss={() => this.setState({ imageError: "" })}>
								{this.state.imageError}
							</Alert>
						)}
						{this.state.levelError && (
							<Alert type="error" dismissible onDismiss={() => this.setState({ levelError: "" })}>
								{this.state.levelError}
							</Alert>
						)}
						<Alert type="warning">{logicLockMessage}</Alert>

						<Tabs
							tabs={[
								{
									label: "General",
									id: "general",
									content: (
										<SpaceBetween direction="vertical" size="l">
											<Container header={<Box variant="h3">General</Box>}>
												<SpaceBetween direction="vertical" size="m">
													<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
														<FormField description="Translation achievement name" label="Achievement Name" stretch>
															<Input
																placeholder="Enter achievement name"
																value={A.name}
																onChange={(e) =>
																	this.props.setAchievementEditModal({
																		...A,
																		name: e.detail.value,
																	})
																}
															/>
														</FormField>

														<FormField description="Name translation type" label="Name translation type" stretch>
															<Select
																selectedOption={A.nameTranslationType ? { value: A.nameTranslationType, label: A.nameTranslationType.charAt(0).toUpperCase() + A.nameTranslationType.slice(1) } : null}
																onChange={(e) => {
																	this.props.setAchievementEditModal({
																		...A,
																		nameTranslationType: e.detail.selectedOption.value,
																	});
																}}
																options={[
																	{ value: "local", label: "Local" },
																	{ value: "international", label: "International" },
																	{ value: "mixed", label: "Mixed" },
																]}
																placeholder="Select achievement name translation type"
															/>
														</FormField>

														<FormField description="Reward description" label="Reward description" stretch>
															<Input
																placeholder="Enter reward description"
																value={A.description}
																onChange={(e) =>
																	this.props.setAchievementEditModal({
																		...A,
																		description: e.detail.value,
																	})
																}
															/>
														</FormField>

														<FormField description="Reward description translation type" label="Reward description translation type" stretch>
															<Select
																selectedOption={A.descriptionTranslationType ? { value: A.descriptionTranslationType, label: A.descriptionTranslationType.charAt(0).toUpperCase() + A.descriptionTranslationType.slice(1) } : null}
																onChange={(e) => {
																	this.props.setAchievementEditModal({
																		...A,
																		descriptionTranslationType: e.detail.selectedOption.value,
																	});
																}}
																options={[
																	{ value: "local", label: "Local" },
																	{ value: "international", label: "International" },
																	{ value: "mixed", label: "Mixed" },
																]}
																placeholder="Select reward description translation type"
															/>
														</FormField>
														<FormField description="Achievement type" label="Achievement type" stretch>
															<Select
																disabled
																selectedOption={A.type}
																onChange={({ detail }) =>
																	this.props.setAchievementEditModal({
																		...A,
																		type: detail.selectedOption,
																	})
																}
																options={[
																	{ value: "daily", label: "daily" },
																	{ value: "unique", label: "unique" },
																	{ value: "progressive", label: "progressive" },
																	{ value: "daily_progressive", label: "daily progressive" },
																]}
																placeholder="Select a type"
															/>
														</FormField>

														{A.type?.value !== "daily_progressive" && (
															<FormField description="Achievement source type" label="Achievement source type" stretch>
																<Select
																	disabled
																	selectedOption={A.sourceType}
																	onChange={({ detail }) => {
																		const newType = detail.selectedOption.value;
																		const updates = { sourceType: detail.selectedOption };
																		if (newType !== "spins") {
																			updates.providerSpinsSelected = null;
																			updates.gameSpinsSelected = null;
																			updates.availableSpinsGames = [];
																		}
																		if (newType === "verify" || newType === "email_verified" || newType === "phone_verified" || newType === "tg_verified") {
																			this.props.setAchievementEditModal({
																				...A,
																				...updates,
																				count: 1,
																				type: null,
																				levels: [],
																			});
																		} else {
																			this.props.setAchievementEditModal({
																				...A,
																				...updates,
																				count: 0,
																			});
																		}
																	}}
																	options={[
																		{ value: "deposit", label: "Deposit" },
																		{ value: "withdrawal", label: "Withdrawal" },
																		{ value: "verify", label: "Verify" },
																		{ value: "email_verified", label: "Email verified" },
																		{ value: "phone_verified", label: "Phone verified" },
																		{ value: "tg_verified", label: "Telegram verified" },
																		{ value: "vip_lvl", label: "Vip lvl" },
																		{ value: "spins", label: "Spins" },
																		{ value: "mobile", label: "Mobile" },
																		{ value: "referral", label: "Referral" },
																		{ value: "tasks", label: "Tasks" },
																	]}
																	placeholder="Select a type"
																/>
															</FormField>
														)}
													</Grid>

													{A.type?.value !== "progressive" && A.type?.value !== "daily_progressive" && (
														<div style={lockedSectionStyle} inert="" aria-disabled="true">
															<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
																<FormField description="Reward type" label="Reward type" stretch>
																	<Select
																		selectedOption={A.rewardType}
																		onChange={({ detail }) => {
																			this.props.setAchievementEditModal({
																				...A,
																				rewardType: detail.selectedOption,
																				gameSelected: null,
																				availableFreespinsGames: [],
																				providerSelected: null,
																				rewardValue: "",
																				betAmount: "",
																				expiresAt: "",
																				chestRewards: [],
																				chestType: "",
																				chestPrizeId: null,
																				chestPrizeSelected: null,
																				chestPrizeMatchMissing: false,
																			});

																			if (detail.selectedOption.value === "chest") {
																				this.loadChestPrizeOptions();
																			}
																		}}
																		options={[
																			{ value: "money", label: "Money" },
																			{ value: "freespins", label: "Freespins" },
																			{ value: "tickets", label: "Tickets" },
																			{ value: "chest", label: "Chest" },
																		]}
																		placeholder="Select a reward type"
																	/>
																</FormField>
																{A.rewardType?.value !== "chest" && (
																	<FormField description="Reward value" label="Reward value" stretch>
																		<Input
																			type="number"
																			placeholder="Enter reward value"
																			value={A.rewardValue}
																			onChange={(e) =>
																				this.props.setAchievementEditModal({
																					...A,
																					rewardValue: e.detail.value,
																				})
																			}
																		/>
																	</FormField>
																)}
															</Grid>
														</div>
													)}

													<FormField description="Achievement order [-...inf] " label="Achievement order" stretch>
														<Input
															type="number"
															placeholder="Enter order"
															value={A.order}
															onChange={(e) =>
																this.props.setAchievementEditModal({
																	...A,
																	order: e.detail.value,
																})
															}
														/>
													</FormField>
												</SpaceBetween>
											</Container>

											{A.sourceType?.value === "spins" && A.type?.value !== "progressive" && A.type?.value !== "daily_progressive" && (
												<div style={lockedSectionStyle} inert="" aria-disabled="true">
													<Container header={<Box variant="h3">Game params</Box>}>
														<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
														<FormField description="Selected provider" stretch label="Provider">
															<Select
																filteringType="auto"
																placeholder="Select Provider"
																selectedOption={A.providerSpinsSelected}
																onChange={(option) => {
																	this.props.setAchievementEditModal({
																		...A,
																		providerSpinsSelected: option.detail.selectedOption,
																		gameSpinsSelected: null,
																		availableSpinsGames: [],
																	});
																	this.loadAvailableSpinsGames(option.detail.selectedOption.type);
																}}
																options={this.props.config.game.options.provider}
															/>
														</FormField>

														{A.providerSpinsSelected && (
															<FormField description="Selected game" stretch label="Game">
																<Select
																	filteringType="auto"
																	placeholder="Select Game (type to search...)"
																	selectedOption={A.gameSpinsSelected}
																	onChange={(option) => {
																		this.props.setAchievementEditModal({
																			...A,
																			gameSpinsSelected: option.detail.selectedOption,
																		});
																	}}
																	options={this.getFilteredSpinsGames()}
																/>
															</FormField>
														)}

														<FormField description="Spins amount only coops" label="Spins amount more than" stretch>
															<Input
																placeholder="Spins amount (coops)"
																type="number"
																value={A.gameSpinsAmount}
																onChange={(e) =>
																	this.props.setAchievementEditModal({
																		...A,
																		gameSpinsAmount: e.detail.value,
																	})
																}
															/>
														</FormField>

														<FormField description="Reset gameId and providerId" label="Reset gameId and ProviderId" stretch>
															<Button
																onClick={() => {
																	this.props.setAchievementEditModal({
																		...A,
																		gameSpinsSelected: null,
																		providerSpinsSelected: null,
																	});
																}}>
																Reset
															</Button>
														</FormField>
														</Grid>
													</Container>
												</div>
											)}

											{A.rewardType?.value === "freespins" && A?.type?.value !== "progressive" && A?.type?.value !== "daily_progressive" && (
												<div style={lockedSectionStyle} inert="" aria-disabled="true">
													<Container header={<Box variant="h3">Freespins params</Box>}>
														<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
														<FormField description="Selected provider" stretch label="Provider">
															<Select
																filteringType="auto"
																placeholder="Select Provider"
																selectedOption={A.providerSelected}
																onChange={(option) => {
																	this.props.setAchievementEditModal({
																		...A,
																		providerSelected: option.detail.selectedOption,
																		gameSelected: null,
																		availableFreespinsGames: [],
																	});
																	this.loadAvailableFreespinsGames(option.detail.selectedOption.type);
																}}
																options={[{ label: "Endorphina", value: "endorphina", type: 10 }]}
															/>
														</FormField>

														{A.providerSelected && (
															<>
																<FormField description="Selected game" stretch label="Game">
																	<Select
																		filteringType="auto"
																		placeholder="Select Game (type to search...)"
																		selectedOption={A.gameSelected}
																		onChange={(option) => {
																			this.props.setAchievementEditModal({
																				...A,
																				gameSelected: option.detail.selectedOption,
																			});
																		}}
																		options={this.getFilteredFreespinsGames()}
																	/>
																</FormField>

																<FormField description="Bet amount (e.g., 100 for 100.00 RUB)" stretch label="Bet Amount">
																	<Input
																		type="number"
																		inputMode="decimal"
																		placeholder="Enter bet amount (e.g., 100 for 100.00 RUB)"
																		value={A.betAmount || ""}
																		onChange={(input) => {
																			this.props.setAchievementEditModal({
																				...A,
																				betAmount: input.detail.value,
																			});
																		}}
																	/>
																</FormField>

																<FormField description="Expired fs date (Number) (1 - day .. eg)" stretch label="Expiration Date">
																	<Input
																		placeholder="Enter expiration date"
																		value={A.expiresAt || ""}
																		onChange={(input) => {
																			this.props.setAchievementEditModal({
																				...A,
																				expiresAt: input.detail.value,
																			});
																		}}
																	/>
																</FormField>
															</>
														)}
														</Grid>
													</Container>
												</div>
											)}

											{A.rewardType?.value === "chest" && A?.type?.value !== "progressive" && A?.type?.value !== "daily_progressive" && (
												<Container header={<Box variant="h3">Chest preset</Box>}>
													<SpaceBetween direction="vertical" size="m">
														<FormField label="Chest" description="Changing the preset affects unclaimed rewards only." stretch>
															<Select
																filteringType="auto"
																placeholder="Select chest preset"
																selectedOption={A.chestPrizeSelected}
																onChange={({ detail }) => this.selectChestPrize(detail.selectedOption)}
																options={A.chestPrizeOptions}
																statusType={A.chestPrizeLoading ? "loading" : "finished"}
																loadingText="Loading chest presets"
																empty="No chest presets found"
															/>
														</FormField>

														{A.chestPrizeMatchMissing && (
															<Alert type="warning">
																Saved chest configuration was not matched to a preset. Select a preset to replace it.
															</Alert>
														)}

														{A.chestType && (
															<Box>{`Chest type: ${formatChestPrizeType(A.chestType)}`}</Box>
														)}

														{(A.chestRewards || []).length > 0 && (
															<Box>{`Rewards in chest: ${(A.chestRewards || []).length}`}</Box>
														)}
													</SpaceBetween>
												</Container>
											)}

											<Container header={<Box variant="h3">Parameters</Box>}>
												<div style={lockedSectionStyle} inert="" aria-disabled="true">
													<Grid gridDefinition={[{ colspan: 12 }]}>
														{A.type?.value !== "progressive" && A.type?.value !== "daily_progressive" && (
															<>
																<FormField description={A.sourceType?.value ? `Achievement count of ${A.sourceType?.value}` : "Achievement count"} label="Count" stretch>
																	<Input
																		type="number"
																		placeholder={A.sourceType?.value ? `Enter count of ${A.sourceType?.value}` : "Enter count"}
																		value={A.count}
																		onChange={(e) =>
																			this.props.setAchievementEditModal({
																				...A,
																				count: e.detail.value,
																			})
																		}
																		disabled={A.sourceType?.value === "verify" || A.sourceType?.value === "email_verified" || A.sourceType?.value === "phone_verified" || A.sourceType?.value === "tg_verified"}
																	/>
																</FormField>
															</>
														)}
													</Grid>

													{A.type?.value !== "progressive" && A.type?.value !== "daily_progressive" && (A.sourceType?.value === "deposit" || A.sourceType?.value === "withdrawal") && (
														<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
															<>
																<FormField description="Deposit amount" label="Amount" stretch>
																	<Input
																		type="number"
																		placeholder="Enter amount"
																		value={A.amount}
																		onChange={(e) =>
																			this.props.setAchievementEditModal({
																				...A,
																				amount: e.detail.value,
																			})
																		}
																	/>
																</FormField>

																<FormField description="Deposit type" label="Type" stretch>
																	<Select
																		selectedOption={A.triggerType}
																		onChange={({ detail }) => {
																			this.props.setAchievementEditModal((prev) => ({
																				...prev,
																				triggerType: detail.selectedOption,
																			}));
																		}}
																		options={depositType}
																		placeholder="Select type"
																	/>
																</FormField>
															</>
														</Grid>
													)}
												</div>

												<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
													<FormField description="Button translation text" label="Button Text" stretch>
														<Input
															placeholder="Enter button text"
															value={A.buttonText}
															onChange={(e) =>
																this.props.setAchievementEditModal({
																	...A,
																	buttonText: e.detail.value,
																})
															}
														/>
													</FormField>
													<FormField description="Button translation type" label="Button translation type" stretch>
														<Select
															selectedOption={A.buttonTranslationType ? { value: A.buttonTranslationType, label: A.buttonTranslationType.charAt(0).toUpperCase() + A.buttonTranslationType.slice(1) } : null}
															onChange={(e) => {
																this.props.setAchievementEditModal({
																	...A,
																	buttonTranslationType: e.detail.selectedOption.value,
																});
															}}
															options={[
																{ value: "local", label: "Local" },
																{ value: "international", label: "International" },
																{ value: "mixed", label: "Mixed" },
															]}
															placeholder="Select button translation type"
														/>
													</FormField>
												</Grid>
											</Container>

											<Container header={<Box variant="h3">Cycle timer</Box>}>
												<div style={lockedSectionStyle} inert="" aria-disabled="true">
													<Grid gridDefinition={A.cycle?.value === "replay" ? [{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }] : [{ colspan: 12 }]}>
													<FormField description="Cycle of achievement" label="Cycle" stretch>
														<Select
															selectedOption={A.cycle}
															onChange={({ detail }) =>
																this.props.setAchievementEditModal({
																	...A,
																	cycle: detail.selectedOption,
																})
															}
															options={[
																{ value: "replay", label: "Replay" },
																{ value: "not", label: "Not repeatable" },
															]}
															placeholder="Select cycle"
														/>
													</FormField>
													{A.cycle?.value === "replay" && (
														<>
															<FormField description="Cycle unit hours of day" label="Cycle Unit" stretch>
																<Select
																	selectedOption={A.cycleUnit}
																	onChange={({ detail }) =>
																		this.props.setAchievementEditModal({
																			...A,
																			cycleUnit: detail.selectedOption,
																		})
																	}
																	options={[
																		{ value: "hour", label: "Hour" },
																		{ value: "day", label: "Day" },
																	]}
																	placeholder="Select unit"
																/>
															</FormField>

															<FormField description="Number of hours of day" label="Cycle Timer" stretch>
																<Input
																	type="number"
																	placeholder="Enter timer"
																	value={A.cycleTimer}
																	onChange={(e) =>
																		this.props.setAchievementEditModal({
																			...A,
																			cycleTimer: e.detail.value,
																		})
																	}
																/>
															</FormField>
														</>
													)}
													</Grid>
												</div>
												{A.cycle?.value === "not" && (
													<Box margin={{ top: "m" }}>
														<FormField description="Date from/to" label="Expired achievement date" stretch>
															<DateRangePicker
															onChange={({ detail }) => {
																this.props.setAchievementEditModal({
																	...A,
																	startDate: detail.value.startDate ? detail.value.startDate.split("+")[0] : "",
																	endDate: detail.value.endDate ? detail.value.endDate.split("+")[0] : "",
																});
															}}
															value={{
																startDate: A.startDate || "",
																endDate: A.endDate || "",
																type: "absolute",
															}}
															relativeOptions={[]}
															isValidRange={(range) => {
																if (range.type === "absolute") {
																	const [startDateWithoutTime] = range.startDate.split("T");
																	const [endDateWithoutTime] = range.endDate.split("T");
																	if (!startDateWithoutTime || !endDateWithoutTime) {
																		return {
																			valid: false,
																			errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
																		};
																	}
																	if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																		return {
																			valid: false,
																			errorMessage: "The selected date range is invalid. The start date must be before the end date.",
																		};
																	}
																} else {
																	return { valid: false };
																}
															}}
															i18nStrings={{
																todayAriaLabel: "Today",
																nextMonthAriaLabel: "Next month",
																previousMonthAriaLabel: "Previous month",
																customRelativeRangeDurationLabel: "Duration",
																customRelativeRangeDurationPlaceholder: "Enter duration",
																customRelativeRangeOptionLabel: "Custom range",
																customRelativeRangeOptionDescription: "Define a custom range in the past",
																customRelativeRangeUnitLabel: "Unit of time",
																formatRelativeRange: ({ amount, unit, startDate, endDate }) => {
																	const start = new Date(startDate);
																	const end = new Date(endDate);

																	const locale = "en-GB";
																	const dateOptions = { year: "numeric", month: "long", day: "numeric" };
																	const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

																	const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
																	const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

																	return `${formattedStart} - ${formattedEnd}`;
																},
																formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
																dateTimeConstraintText: "Date and time must be within the selected range.",
																renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
																labelRelativeRange: "Relative range",
																labelAbsoluteRange: "Absolute range",
																dateTimePickerAriaLabel: "Date and time picker",
																dateTimePickerLabel: "Date and Time",
																clearButtonAriaLabel: "Clear date range",
																relativeModeTitle: "Relative mode",
																absoluteModeTitle: "Absolute mode",
																cancelButtonLabel: "Cancel",
																applyButtonLabel: "Apply",
															}}
															placeholder="Expired date from/to"
															/>
														</FormField>
													</Box>
												)}
											</Container>

											<Container header={<Box variant="h3">Other</Box>}>
												<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
													<FormField description="Link button text" label="Link" stretch>
														<Input
															type="url"
															placeholder="Enter URL"
															value={A.link}
															onChange={(e) =>
																this.props.setAchievementEditModal({
																	...A,
																	link: e.detail.value,
																})
															}
														/>
													</FormField>

													<FormField description="Achievement active" label="Active" stretch>
														<Select
															selectedOption={A.active}
															onChange={({ detail }) =>
																this.props.setAchievementEditModal({
																	...A,
																	active: detail.selectedOption,
																})
															}
															options={[
																{ value: "1", label: "Enabled" },
																{ value: "0", label: "Disabled" },
															]}
															placeholder="Select active"
														/>
													</FormField>
												</Grid>
											</Container>
										</SpaceBetween>
									),
								},

								// ===== Cohort =====
								{
									label: "Cohort",
									id: "cohort",
									content: (
										<div style={lockedSectionStyle} inert="" aria-disabled="true">
											<SpaceBetween size="l">
												<Box>
													<Button variant="primary" iconName="add-plus" onClick={this.addCohortGroup}>
														Add group
													</Button>
												</Box>

												{(this.state.cohortGroups || []).map((group, groupIndex) => {
												const moduleOptions = this.getCohortModuleOptions();
												const logicalOperatorOptions = this.getLogicalOperatorOptions();

												const selectedModule = moduleOptions.find((o) => o.value === group.module) || null;

												const groupOperatorValue = groupIndex === 0 ? "AND" : group.groupLogicalOperator || "AND";
												const selectedGroupLogicalOperator = logicalOperatorOptions.find((o) => o.value === groupOperatorValue) || null;

												const groupConditions = (A.conditions || []).filter((c) => c.groupId === group.groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

												const newCondition = this.state.newConditionByGroupId?.[group.groupId] || {};
												const selectedConditionLogicalOperator = logicalOperatorOptions.find((o) => o.value === (newCondition.logicalOperator || "AND")) || null;

												const fieldOptions = this.getCohortFieldOptionsByModule(group.module);

												const selectedFieldOption = newCondition.field ? { value: newCondition.field, label: newCondition.fieldLabel } : null;

												const selectedOperatorOption = newCondition.operator ? { value: newCondition.operator, label: newCondition.operator } : null;

												const isAddDisabled = !newCondition.field || this.isConditionValueEmpty(newCondition.value) || (!newCondition.operator && newCondition.conditionalType !== "select" && newCondition.conditionalType !== "multiselect");

												return (
													<Container
														key={group.groupId}
														header={
															<Header
																variant="h3"
																actions={
																	<Button variant="icon" iconName="delete-marker" onClick={() => this.removeCohortGroup(group.groupId)}>
																		Remove group
																	</Button>
																}>
																{`Group ${groupIndex + 1}`}
															</Header>
														}>
														<SpaceBetween size="m">
															<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
																<FormField label="Module" stretch>
																	<Select selectedOption={selectedModule} onChange={({ detail }) => this.changeCohortGroupModule(group.groupId, detail.selectedOption.value)} options={moduleOptions} placeholder="Select module" />
																</FormField>

																<FormField label="Group operator" stretch>
																	<Select
																		selectedOption={selectedGroupLogicalOperator}
																		onChange={({ detail }) => {
																			if (groupIndex === 0) return;
																			this.updateCohortGroup(group.groupId, {
																				groupLogicalOperator: detail.selectedOption.value,
																			});
																		}}
																		options={logicalOperatorOptions}
																		placeholder="Select operator"
																		disabled={groupIndex === 0}
																	/>
																</FormField>

																<Box />
															</Grid>

															<Table
																columnDefinitions={[
																	{ id: "logic", header: "Logic", cell: (item) => item.logicalOperator || "-" },
																	{ id: "field", header: "Field", cell: (item) => item.fieldLabel || item.field },
																	{ id: "operator", header: "Operator", cell: (item) => item.operator },
																	{ id: "value", header: "Value", cell: (item) => Array.isArray(item.value) ? item.value.join(", ") : item.value },
																	{
																		id: "actions",
																		header: "Control",
																		cell: (item) => (
																			<Button
																				variant="icon"
																				iconName="delete-marker"
																				onClick={() => {
																					const next = this.normalizeGroupConditions(
																						(A.conditions || []).filter((c) => {
																							if (typeof c.id !== "undefined") return c.id !== item.id;
																							return c?.deleteId !== item?.deleteId;
																						}),
																						group.groupId,
																					);
																					this.props.setAchievementEditModal({ ...A, conditions: next });
																				}}>
																				Remove
																			</Button>
																		),
																	},
																]}
																footer={
																	<FormField stretch label="Add condition">
																		<Grid gridDefinition={groupConditions.length ? [{ colspan: 2 }, { colspan: 4 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }] : [{ colspan: 4 }, { colspan: 3 }, { colspan: 3 }, { colspan: 2 }]}>
																			{groupConditions.length > 0 && <Select selectedOption={selectedConditionLogicalOperator} placeholder="AND/OR" options={logicalOperatorOptions} onChange={({ detail }) => this.updateNewCondition(group.groupId, { logicalOperator: detail.selectedOption.value })} />}

																			<Select
																				selectedOption={selectedFieldOption}
																				placeholder="Select field"
																				options={fieldOptions}
																				onChange={({ detail }) =>
																					this.updateNewCondition(group.groupId, {
																						field: detail.selectedOption.value,
																						fieldLabel: detail.selectedOption.label,
																						conditionalType: detail.selectedOption.type,
																						specialType: detail.selectedOption.specialType,
																						value: null,
																						operator: detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect" ? "=" : null,
																					})
																				}
																			/>

																			<Select
																				selectedOption={selectedOperatorOption}
																				placeholder="Select operator"
																				disabled={newCondition.conditionalType === "select" || newCondition.conditionalType === "multiselect"}
																				options={[
																					{ value: "=", label: "=" },
																					{ value: ">", label: ">" },
																					{ value: "<", label: "<" },
																					{ value: ">=", label: ">=" },
																					{ value: "<=", label: "<=" },
																					{ value: "IN", label: "IN" },
																				]}
																				onChange={({ detail }) => this.updateNewCondition(group.groupId, { operator: detail.selectedOption.value })}
																			/>

																			<Box>{this.renderComponentFromType(group.groupId, newCondition.conditionalType, newCondition.specialType)}</Box>

																			<Button disabled={isAddDisabled} onClick={() => this.addCohortCondition(group, groupIndex)}>
																				+ Add
																			</Button>
																		</Grid>
																	</FormField>
																}
																items={groupConditions}
																empty="No conditions set"
															/>
														</SpaceBetween>
													</Container>
												);
											})}
											</SpaceBetween>
										</div>
									),
								},

								// ===== Levels =====
								{
									label: "Levels",
									id: "levels",

									content:
										A.type?.value === "progressive" && !["verify", "email_verified", "phone_verified", "tg_verified"].includes(A.sourceType?.value) ? (
											<div style={lockedSectionStyle} inert="" aria-disabled="true">
												<Table
													columnDefinitions={[
														{ id: "level", header: "Level", cell: (item) => item.level },
														{
															id: "progress_required",
															header: "Count",
															minWidth: 140,
															cell: (item) => (
																<Input
																	value={item.progress_required}
																	onChange={(e) => {
																		const v = Number(e.detail.value) || 0;
																		const newLvls = (A.levels || []).map((el) => (el.id === item.id ? { ...el, progress_required: v } : el));
																		this.props.setAchievementEditModal({ ...A, levels: newLvls });
																	}}
																/>
															),
														},
														{
															id: "rewardType",
															header: "Reward Type",
															cell: (item) => (
																<>
																	<Button
																		wrapText={false}
																		variant="link"
																		onClick={() => {
																			this.setState({ rewardTypeEdit: typeof item.deleteId !== "undefined" ? item.deleteId : item.id, rewardTypeValue: item.rewardType });
																		}}>
																		{item.rewardType ? item.rewardType.charAt(0).toUpperCase() + item.rewardType.slice(1) : "N/A"}
																	</Button>
																	{this.state.rewardTypeEdit && (
																		<Modal
																			visible={true}
																			header="Edit Reward Type"
																			footer={
																				<Box float="right">
																					<Button variant="link" onClick={() => this.setState({ rewardTypeEdit: null })}>
																						Cancel
																					</Button>
																					<Button
																						variant="primary"
																						onClick={() => {
																							this.setState({ rewardTypeEdit: null });
																						}}>
																						Save
																					</Button>
																				</Box>
																			}
																			onDismiss={() => {
																				this.setState({ rewardTypeEdit: null });
																			}}>
																			<SpaceBetween size="m">
																				<FormField label="RewardType">
																					<Select
																						selectedOption={this.state.rewardTypeValue ? { value: this.state.rewardTypeValue, label: this.state.rewardTypeValue.charAt(0).toUpperCase() + this.state.rewardTypeValue.slice(1) } : null}
																						onChange={({ detail }) => {
																							const val = detail.selectedOption.value;
																							const newLvls = (A.levels || []).map((el) => {
																								if (typeof el.deleteId !== "undefined" ? el.deleteId !== this.state.rewardTypeEdit : el.id !== this.state.rewardTypeEdit) return el;
																								const next = { ...el, rewardType: val };
																								if (val !== "freespins") {
																									delete next.freespins;
																								} else {
																									next.freespins = el.freespins || { providerId: "", gameId: "", betAmount: 0, expiresAt: "" };
																								}
																								if (val !== "chest" && next.rewardConditions?.prizes) {
																									next.rewardConditions = {};
																								}
																								if (val === "chest") {
																									next.rewardValue = 1;
																									next.rewardConditions = next.rewardConditions?.prizes ? next.rewardConditions : { prizes: [] };
																								}
																								if (val === "tickets" && next.rewardValue == null) {
																									next.rewardValue = Number(next.rewardValue) || 0;
																								}
																								return next;
																							});

																							this.setState({ rewardTypeValue: detail.selectedOption.value });
																							this.props.setAchievementEditModal({ ...A, levels: newLvls });
																						}}
																						options={[
																							{ value: "money", label: "Money" },
																							{ value: "tickets", label: "Tickets" },
																							{ value: "freespins", label: "Freespins" },
																							{ value: "chest", label: "Chest" },
																						]}
																					/>
																				</FormField>
																			</SpaceBetween>
																		</Modal>
																	)}
																</>
															),
														},
														{
															id: "rewardValue",
															header: "Reward Value",
															cell: (item) =>
																item.rewardType === "chest" ? (
																	<Box>{this.getChestDisplayLabel(item.rewardConditions)}</Box>
																) : (
																	<Input
																		type="number"
																		value={item.rewardValue ?? ""}
																		onChange={(e) => {
																			const v = e.detail.value === "" ? "" : Number(e.detail.value);
																			const newLvls = (A.levels || []).map((el) => (el.id === item.id ? { ...el, rewardValue: v } : el));
																			this.props.setAchievementEditModal({ ...A, levels: newLvls });
																		}}
																	/>
																),
														},
														{
															id: "fs_edit",
															header: "Freespins",
															cell: (item) =>
																item.rewardType === "freespins" ? (
																	<Button wrapText={false} variant="link" onClick={() => this.openFsModal(item)}>
																		Edit
																	</Button>
																) : (
																	<Button wrapText={false} variant="link" disabled>
																		-
																	</Button>
																),
														},
														{
															id: "chest_edit",
															header: "Chest",
															cell: (item) =>
																item.rewardType === "chest" ? (
																	<Button wrapText={false} variant="link" onClick={() => this.openChestModal(item)}>
																		Edit
																	</Button>
																) : (
																	<Button wrapText={false} variant="link" disabled>
																		-
																	</Button>
																),
														},
														...(typeValue === "spins"
															? [
																	{
																		id: "spins_edit",
																		header: "Spins",
																		cell: (item) => (
																			<Button
																				wrapText={false}
																				variant="link"
																				onClick={() => {
																					this.openSpinsModal(item);
																				}}>
																				Edit
																			</Button>
																		),
																	},
															  ]
															: []),
														...(A.sourceType?.value === "deposit" || A.sourceType?.value === "withdrawal"
															? [
																	{
																		id: "amount",
																		header: "Amount",
																		cell: (item) => (
																			<Input
																				type="number"
																				value={item.amount ?? ""}
																				onChange={(e) => {
																					const v = e.detail.value === "" ? "" : Number(e.detail.value);
																					const newLvls = (A.levels || []).map((el) => (el.id === item.id ? { ...el, amount: v } : el));
																					this.props.setAchievementEditModal({ ...A, levels: newLvls });
																				}}
																			/>
																		),
																	},
																	{
																		id: "trigger_type",
																		header: "Trigger Type",
																		cell: (item) => (
																			<>
																				<Button wrapText={false} variant="link" onClick={() => this.setState({ editingRow: item.id, editingValue: item.trigger_type || "both" })}>
																					{item.trigger_type ? item.trigger_type.charAt(0).toUpperCase() + item.trigger_type.slice(1) : "Select type"}
																				</Button>
																				{this.state.editingRow === item.id && (
																					<Modal
																						visible={true}
																						header="Edit Trigger Type"
																						footer={
																							<Box float="right">
																								<Button variant="link" onClick={() => this.setState({ editingRow: null })}>
																									Cancel
																								</Button>
																								<Button
																									variant="primary"
																									onClick={() => {
																										const newLvls = (A.levels || []).map((el) => (el.id === this.state.editingRow ? { ...el, trigger_type: this.state.editingValue } : el));
																										this.props.setAchievementEditModal({ ...A, levels: newLvls });
																										this.setState({ editingRow: null });
																									}}>
																									Save
																								</Button>
																							</Box>
																						}
																						onDismiss={() => this.setState({ editingRow: null })}>
																						<Select
																							selectedOption={{ value: this.state.editingValue, label: this.state.editingValue.charAt(0).toUpperCase() + this.state.editingValue.slice(1) }}
																							onChange={({ detail }) => this.setState({ editingValue: detail.selectedOption.value })}
																							options={[
																								{ value: "crypto", label: "Crypto" },
																								{ value: "fiat", label: "Fiat" },
																								{ value: "both", label: "Both" },
																							]}
																						/>
																					</Modal>
																				)}
																			</>
																		),
																	},
															  ]
															: []),
														{
															id: "actions",
															header: "Control",
															cell: (item, i) => (
																<Button
																	variant="icon"
																	iconName="delete-marker"
																	onClick={() => {
																		const newLvls = [...(A.levels || [])].filter((element) => {
																			if (typeof element.id !== "undefined") {
																				return element.id !== item.id;
																			} else {
																				return element?.deleteId !== item?.deleteId;
																			}
																		});
																		this.props.setAchievementEditModal({ ...A, levels: newLvls });
																	}}>
																	Remove
																</Button>
															),
														},
													]}
													items={A.levels || []}
													footer={
														<FormField stretch>
															<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, ...(A.sourceType?.value === "deposit" || A.sourceType?.value === "withdrawal" ? [{ colspan: 6 }, { colspan: 6 }] : []), { colspan: 6 }, { colspan: 6 }, ...(A.sourceType?.value === "spins" ? [{ colspan: 6 }, { colspan: 6 }] : []), { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
																<FormField label="Level" description="Achievement lvl">
																	<Input type="number" placeholder="Level" value={this.state?.newLevel || ""} onChange={(e) => this.setState({ newLevel: e.detail.value })} />
																</FormField>
																<FormField label="Count" description="Achievement count">
																	<Input type="number" placeholder="Count" value={this.state?.newProgress || ""} onChange={(e) => this.setState({ newProgress: e.detail.value })} />
																</FormField>

																{(A.sourceType?.value === "deposit" || A.sourceType?.value === "withdrawal") && (
																	<>
																		<FormField label="Amount" description={A.sourceType?.value === "deposit" ? "Deposit amount" : "Withdrawal amount"}>
																			<Input type="number" placeholder="Enter amount" value={this.state?.newAmount || ""} onChange={(e) => this.setState({ newAmount: e.detail.value })} />
																		</FormField>
																		<FormField label={A.sourceType?.value === "deposit" ? "Deposit type" : "Withdrawal type"} description={A.sourceType?.value === "deposit" ? "Deposit type" : "Withdrawal type"}>
																			<Select
																				selectedOption={this.state?.newTriggerType}
																				onChange={({ detail }) => this.setState({ newTriggerType: detail.selectedOption })}
																				options={[
																					{ value: "crypto", label: "Crypto" },
																					{ value: "fiat", label: "Fiat" },
																					{ value: "both", label: "Both" },
																				]}
																				placeholder="Select type"
																			/>
																		</FormField>
																	</>
																)}

																<FormField label="Reward type" description="Achievement reward type">
																	<Select
																		selectedOption={this.state?.newRewardType}
																		onChange={({ detail }) => {
																			this.setState({
																				newRewardType: detail.selectedOption,
																				newRewardValue: "",
																				newProvider: null,
																				newGame: null,
																				newBetAmount: "",
																				newExpiresAt: "",
																				newChestRewards: [],
																				newChestType: "",
																				newChestPrizeId: null,
																				newChestPrizeSelected: null,
																				newSpinsProvider: null,
																				newSpinsGame: null,
																				availableLevelSpinsGames: [],
																			});

																			if (detail.selectedOption.value === "chest") {
																				this.loadChestPrizeOptions();
																			}
																		}}
																		options={[
																			{ value: "money", label: "Money" },
																			{ value: "tickets", label: "Tickets" },
																			{ value: "freespins", label: "Freespins" },
																			{ value: "chest", label: "Chest" },
																		]}
																		placeholder="Reward type"
																	/>
																</FormField>

																{(this.state?.newRewardType?.value === "money" || this.state?.newRewardType?.value === "tickets") && (
																	<FormField label="Reward value" description="Achievement reward value">
																		<Input type="number" placeholder="Reward value" value={this.state?.newRewardValue || ""} onChange={(e) => this.setState({ newRewardValue: e.detail.value })} />
																	</FormField>
																)}

																{this.state?.newRewardType?.value === "freespins" && (
																	<>
																		<FormField label="Freespins Provider" description="Provider">
																			<Select
																				filteringType="auto"
																				placeholder="Provider"
																				selectedOption={this.state?.newProvider}
																				onChange={({ detail }) => {
																					this.setState({
																						newProvider: detail.selectedOption,
																						newGame: null,
																					});
																					this.loadAvailableGames(detail.selectedOption.value);
																				}}
																				options={[{ value: "endorphina", label: "Endorphina" }]}
																			/>
																		</FormField>
																		<FormField label="Freespins game" description="Game">
																			<Select disabled={!this.state?.newProvider} placeholder="Game" filteringType="auto" selectedOption={this.state?.newGame} options={this.getFilteredGames()} onChange={({ detail }) => this.setState({ newGame: detail.selectedOption })} />
																		</FormField>
																		<FormField label="Freespins bet amount" description="Bet amount">
																			<Input type="number" placeholder="Bet Amount" value={this.state?.newBetAmount || ""} onChange={(e) => this.setState({ newBetAmount: e.detail.value })} />
																		</FormField>
																		<FormField label="Freespins expires at" description="Expires at (number)">
																			<Input placeholder="Expires At (timestamp/int)" value={this.state?.newExpiresAt || ""} onChange={(e) => this.setState({ newExpiresAt: e.detail.value })} />
																		</FormField>
																		<FormField label="Freespins reward value" description="Freespins count">
																			<Input type="number" placeholder="Reward Value (spins 1-1000)" value={this.state?.newRewardValue || ""} onChange={(e) => this.setState({ newRewardValue: e.detail.value })} />
																		</FormField>
																	</>
																)}

																{typeValue === "spins" && (
																	<>
																		<FormField label="Provider" description="Provider">
																			<Select
																				filteringType="auto"
																				placeholder="Provider"
																				selectedOption={this.state?.newSpinsProvider}
																				onChange={({ detail }) => {
																					this.setState({
																						newSpinsProvider: detail.selectedOption,
																						newSpinsGame: null,
																						availableLevelSpinsGames: [],
																					});
																					this.loadAvailableLevelSpinsGames(detail.selectedOption.type);
																				}}
																				options={this.props.config.game.options.provider}
																			/>
																		</FormField>
																		<FormField label="Game" description="Game">
																			<Select disabled={!this.state?.newSpinsProvider} placeholder="Game" filteringType="auto" selectedOption={this.state?.newSpinsGame} options={this.getFilteredLevelSpinsGames()} onChange={({ detail }) => this.setState({ newSpinsGame: detail.selectedOption })} />
																		</FormField>

																		<FormField label="Spins amount more than" description="Spins amount only coops" stretch>
																			<Input placeholder="Spins amount (coops)" type="number" value={this.state?.newSpinsAmount} onChange={(e) => this.setState({ newSpinsAmount: e.detail.value })} />
																		</FormField>
																	</>
																)}

																<FormField label="Add lvl" description="Add lvl">
																	<Button
																		onClick={() => {
																			const err = this.validateNewLevel();
																			if (err) {
																				this.setState({ levelError: err });
																				return;
																			}

																			const rewardType = this.state?.newRewardType?.value;
																			const levelObj = {
																				level: Number(this.state.newLevel),
																				progress_required: Number(this.state.newProgress) || 0,
																				...(typeValue === "deposit" || typeValue === "withdrawal"
																					? {
																							amount: Number(this.state.newAmount) || 0,
																							trigger_type: this.state.newTriggerType?.value || "both",
																					  }
																					: {}),
																				...(typeValue === "spins"
																					? {
																							providerId: this.state?.newSpinsProvider?.type || null,
																							gameId: this.state?.newSpinsGame?.gameId || null,
																							amount: Number(this.state?.newSpinsAmount || 0) || 0,
																					  }
																					: {}),
																			};

																			if (rewardType === "freespins") {
																				levelObj.rewardType = "freespins";
																				levelObj.rewardValue = Number(this.state.newRewardValue) || 0;
																				levelObj.freespins = {
																					providerId: this.state.newGame?.game.providerID,
																					gameId: this.state.newGame?.game.id,
																					betAmount: Number(this.state.newBetAmount) || 0,
																					expiresAt: parseInt(this.state.newExpiresAt) || 0,
																					currency: "RUB",
																				};
																			} else if (rewardType === "money") {
																				levelObj.rewardType = "money";
																				levelObj.rewardValue = Number(this.state.newRewardValue) || 0;
																			} else if (rewardType === "tickets") {
																				levelObj.rewardType = "tickets";
																				levelObj.rewardValue = Number(this.state.newRewardValue) || 0;
																				levelObj.tickets_reward = Number(this.state.newRewardValue) || 0;
																			} else if (rewardType === "chest") {
																				levelObj.rewardType = "chest";
																				levelObj.rewardValue = 1;
																				levelObj.rewardConditions = {
																					chestPrizeId: Number(this.state.newChestPrizeId),
																				};
																			}

																			levelObj.deleteId = crypto.randomUUID();

																			this.props.setAchievementEditModal({
																				...A,
																				levels: [...(A.levels || []), levelObj],
																			});

																			this.setState({
																				newLevel: "",
																				newProgress: "",
																				newAmount: "",
																				newTriggerType: undefined,
																				newRewardType: undefined,
																				newRewardValue: "",
																				newProvider: null,
																				newGame: null,
																				newSpinsAmount: null,
																				newBetAmount: "",
																				newExpiresAt: "",
																				newChestRewards: [],
																				newChestType: "",
																				newChestPrizeId: null,
																				newChestPrizeSelected: null,
																				newSpinsProvider: null,
																				newSpinsGame: null,
																				availableLevelSpinsGames: [],
																				levelError: "",
																			});
																		}}>
																		+ Add Level
																	</Button>
																</FormField>
															</Grid>

															{this.state?.newRewardType?.value === "chest" && this.renderNewLevelChestConfig()}
														</FormField>
													}
													empty="No levels yet"
												/>

												{fsEditingRow && (
													<Modal
														visible={true}
														header="Edit Freespins"
														footer={
															<Box float="right">
																<Button variant="link" onClick={this.closeFsModal}>
																	Cancel
																</Button>
																<Button variant="primary" onClick={this.saveFsModal}>
																	Save
																</Button>
															</Box>
														}
														onDismiss={this.closeFsModal}>
														<SpaceBetween size="m">
															<FormField label="Provider">
																<Select
																	filteringType="auto"
																	placeholder="Select Provider"
																	selectedOption={fsEditing?.providerSelected || null}
																	onChange={({ detail }) => {
																		const providerSelected = detail.selectedOption;
																		this.loadAvailableGames(providerSelected.value);
																		this.setState({ fsEditing: { ...(fsEditing || {}), providerSelected, gameSelected: null } });
																	}}
																	options={[{ value: "endorphina", label: "Endorphina" }]}
																/>
															</FormField>

															<FormField label="Game">
																<Select
																	filteringType="auto"
																	placeholder="Select Game"
																	selectedOption={fsEditing?.gameSelected || null}
																	options={this.getFilteredGames()}
																	onChange={({ detail }) => {
																		this.setState({ fsEditing: { ...(fsEditing || {}), gameSelected: detail.selectedOption } });
																	}}
																/>
															</FormField>

															<FormField label="Bet Amount">
																<Input type="number" inputMode="decimal" placeholder="Enter bet amount" value={fsEditing?.betAmount ?? ""} onChange={(e) => this.setState({ fsEditing: { ...(fsEditing || {}), betAmount: e.detail.value } })} />
															</FormField>

															<FormField label="Expires At">
																<Input placeholder="Enter expiration (timestamp/int or date token)" value={fsEditing?.expiresAt ?? ""} onChange={(e) => this.setState({ fsEditing: { ...(fsEditing || {}), expiresAt: e.detail.value } })} />
															</FormField>
														</SpaceBetween>
													</Modal>
												)}

												{chestEditingRow && (
													<Modal
														visible={true}
														header="Edit Chest"
														footer={
															<Box float="right">
																<Button variant="link" onClick={this.closeChestModal}>
																	Cancel
																</Button>
																<Button variant="primary" onClick={this.saveChestModal}>
																	Save
																</Button>
															</Box>
														}
														onDismiss={this.closeChestModal}>
														<SpaceBetween size="m">
															<FormField label="Chest" stretch>
																<Select
																	filteringType="auto"
																	placeholder="Select chest preset"
																	selectedOption={chestEditingPrizeSelected}
																	onChange={({ detail }) => this.selectEditingChestPrize(detail.selectedOption)}
																	options={A.chestPrizeOptions}
																	statusType={A.chestPrizeLoading ? "loading" : "finished"}
																	loadingText="Loading chest presets"
																	empty="No chest presets found"
																/>
															</FormField>

															{chestEditingMatchMissing && (
																<Alert type="warning">
																	Saved chest configuration was not matched to a preset. Select a preset to replace it.
																</Alert>
															)}

															{chestEditingType && (
																<Box>{`Chest type: ${formatChestPrizeType(chestEditingType)}`}</Box>
															)}

															{(chestEditingRewards || []).length > 0 && (
																<Box>{`Rewards in chest: ${(chestEditingRewards || []).length}`}</Box>
															)}
														</SpaceBetween>
													</Modal>
												)}

												{spinsEditingRow && (
													<Modal
														visible={true}
														header="Edit Spins"
														footer={
															<Box float="right">
																<Button variant="link" onClick={this.closeSpinsModal}>
																	Cancel
																</Button>
																<Button variant="primary" onClick={this.saveSpinsModal}>
																	Save
																</Button>
															</Box>
														}
														onDismiss={this.closeSpinsModal}>
														<SpaceBetween size="m">
															<FormField description="Selected provider" label="Provider">
																<Select
																	filteringType="auto"
																	placeholder="Select Provider"
																	selectedOption={spinsEditing?.providerSelected || null}
																	onChange={({ detail }) => {
																		const providerSelected = detail.selectedOption;
																		const gameState = this.props.game;
																		let availableGames = [];
																		let gameSelected = null;
																		if (gameState?.loaded && gameState?.data) {
																			const filteredGames = gameState.data.filter((g) => g.providerID === providerSelected.type && g.active === 1);
																			availableGames = filteredGames.map((g) => ({
																				id: g.gameID,
																				description: g.name,
																				name: g.name,
																				gameID: g.gameID,
																				providerID: g.providerID,
																			}));
																			if (spinsEditing.gameSelected?.game.providerID === providerSelected.type) {
																				gameSelected = spinsEditing.gameSelected;
																			}
																		}
																		this.setState({
																			spinsEditing: {
																				...spinsEditing,
																				providerSelected,
																				gameSelected,
																				availableGames,
																			},
																		});
																	}}
																	options={this.props.config.game.options.provider}
																/>
															</FormField>

															<FormField description="Selected game" label="Game">
																<Select
																	filteringType="auto"
																	placeholder="Select Game"
																	selectedOption={spinsEditing?.gameSelected || null}
																	options={
																		spinsEditing?.availableGames
																			? spinsEditing.availableGames.map((g) => ({
																					label: g.description,
																					value: g.description,
																					gameId: g.id,
																					game: g,
																			  }))
																			: []
																	}
																	onChange={({ detail }) => {
																		this.setState({ spinsEditing: { ...(spinsEditing || {}), gameSelected: detail.selectedOption } });
																	}}
																/>
															</FormField>

															<FormField description="Spins amount only coops" label="Spins amount more than" stretch>
																<Input placeholder="Spins amount (coops)" type="number" value={spinsEditing.amount} onChange={(e) => this.setState({ spinsEditing: { ...(spinsEditing || {}), amount: e.detail.value } })} />
															</FormField>
															<FormField description="Reset gameId and providerId" label="Reset gameId and ProviderId" stretch>
																<Button
																	onClick={() => {
																		this.setState({ spinsEditing: { ...(spinsEditing || {}), providerSelected: null, gameSelected: null } });
																	}}>
																	Reset
																</Button>
															</FormField>
														</SpaceBetween>
													</Modal>
												)}
											</div>
										) : (
											<Alert type="info">Levels are available only for progressive achievements or supports type.</Alert>
										),
								},

								{
									label: "Media",
									id: "media",
									content: (
										<SpaceBetween direction="vertical" size="m">
											<FormField label="Image" description="Upload an SVG or PNG image" stretch>
												<input type="file" accept="image/svg+xml,image/png" onChange={this.handleImageChange} />
												{A.image && (
													<Box margin={{ top: "s" }} textAlign="center">
														<img src={A.image} alt="Achievement preview" style={{ maxWidth: "100%", maxHeight: "150px", padding: "4px" }} />
													</Box>
												)}
											</FormField>
											{this.renderJsonImagePreview()}
										</SpaceBetween>
									),
								},
								{
									label: "JSON",
									id: "json",
									content: (
										<SpaceBetween direction="vertical" size="m">
											<Container header={<Box variant="h3">Achievement JSON</Box>}>
												<SpaceBetween direction="vertical" size="m">
													<SpaceBetween direction="horizontal" size="xs">
														<Button onClick={this.copyAchievementJson}>Copy JSON</Button>
														<Button variant="primary" onClick={this.applyAchievementJson}>
															Apply JSON
														</Button>
													</SpaceBetween>
													<Alert type="info">Apply JSON updates only fields allowed in edit mode. Business logic fields remain read-only.</Alert>
													{this.renderJsonImagePreview()}
													{this.state.jsonError && (
														<Alert type="error" dismissible onDismiss={() => this.setState({ jsonError: "" })}>
															{this.state.jsonError}
														</Alert>
													)}
													<FormField label="JSON" stretch>
														<Textarea
															value={this.getJsonTextareaValue()}
															onChange={({ detail }) => this.setState({ jsonText: detail.value, jsonError: "", jsonDirty: true })}
															placeholder="Paste achievement JSON here"
															rows={18}
														/>
													</FormField>
												</SpaceBetween>
											</Container>
										</SpaceBetween>
									),
								},
								...(A.type?.value === "daily_progressive"
									? [
											{
												label: "Daily Progressive",
												id: "daily_progressive",
												content: (
													<div style={lockedSectionStyle} inert="" aria-disabled="true">
														<DailyProgressiveTab
															config={this.state.dailyProgressiveConfig}
															newMilestone={this.state.newMilestone}
															providerOptions={this.props.config.game.options.provider}
															spinsGamesOptions={this.getFilteredDailyConditionsSpinsGames()}
															milestoneGamesOptions={this.getFilteredMilestoneGames()}
															chestPrizeOptions={this.props.achievementEditModal.chestPrizeOptions}
															chestPrizeLoading={this.props.achievementEditModal.chestPrizeLoading}
															onConfigChange={this.updateDailyProgressiveConfig}
															onDailyConditionsChange={this.updateDailyConditions}
															onDepositConditionsChange={this.updateDailyConditionsDeposit}
															onSpinsConditionsChange={this.updateDailyConditionsSpins}
															onLoadSpinsGames={(provider) => {
																if (provider) {
																	this.loadDailyConditionsSpinsGames(provider);
																} else {
																	this.setState({ dailyConditionsSpinsGames: [] });
																}
															}}
															onNewMilestoneChange={this.updateNewMilestone}
															onAddMilestone={this.addMilestone}
															onRemoveMilestone={this.removeMilestone}
															onLoadMilestoneGames={this.loadAvailableMilestoneGames}
															onSelectChestPrize={this.selectDailyMilestoneChestPrize}
															onLoadChestPrizeOptions={this.loadChestPrizeOptions}
														/>
													</div>
												),
											},
									  ]
									: []),
							]}
						/>
					</Form>
				) : (
					<Box textAlign="center">
						<Spinner size="large" />
					</Box>
				)}
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const achievementEditModal = useRecoilValue(achievementEditModalStore);
		const setAchievementEditModal = useSetRecoilState(achievementEditModalStore);
		const game = useRecoilValue(gameStore);
		const config = useRecoilValue(configStore);
		return <Component {...props} config={config} game={game} achievementEditModal={achievementEditModal} setAchievementEditModal={setAchievementEditModal} />;
	};
}

export default withRecoil(AchievementEditModal);
